// Redesigned RPM Overview component matching evitals-ui design
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPrint,
  faDownload,
  faEnvelope,
  faUndo,
  faSearch,
  faChevronUp,
  faChevronDown,
  faCheck,
  faUserPlus,
  faTimes,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea
} from 'recharts';

import AddBPForm from '../../MeasurementData/BloodPressure/AddBPForm';
import AddBGForm from '../../MeasurementData/BloodGlucose/AddBGForm';
import AddWForm from '../../MeasurementData/Weight/AddWForm';
import EmailModal from '../../../components/EmailModal/EmailModal';
import MeanChart from '../../MeasurementData/BloodPressure/Graphs/MeanChart';
import MeanChartBG from '../../MeasurementData/BloodGlucose/Graphs/MeanChart';
import MeanChartWeight from '../../MeasurementData/Weight/Graphs/MeanChartWeight';
import MeanChartFat from '../../MeasurementData/Weight/Graphs/MeanChartFat';
import PieChartWeight from '../../MeasurementData/Weight/Graphs/PieChartWeight';
import PieChartFat from '../../MeasurementData/Weight/Graphs/PieChartFat';
import '../../MeasurementData/MeasurementShell.css';
import './PatientDetails.css';
import '../../../components/Common/ActionButtons/ActionButtons.css';
import '../../../components/PatientReviewCell/PatientReviewCell.css';
import { checkBPValues, checkBGValue, checkWeightValue, checkFatValue, getBmiCategoryStatus, getVitalStatusColor, checkPulseValue, kgToLb } from '../../../utils/measurementUtils';
import { isAiBasedAnalysisEnabled } from '../../../utils/aiBasedAnalysis';
import PulseIcon from '../../../components/Common/PulseIcon/PulseIcon';
import {
  getBloodPressureTargetForMeasurement,
  getBloodGlucoseTargetForMeasurement,
  buildPeriodWindowsFromSchedule,
  matchMeasurementToPeriod,
  getPrimaryBloodPressureTarget,
  getPrimaryBloodGlucoseTarget,
  formatBloodPressureTargetLabel,
  formatBloodGlucoseTargetLabel,
  formatWeightTargetLabel,
  getAiTargetBounds,
  normalizePeriodName
} from '../../../utils/scheduleTargetUtils';
import { printElement, downloadBlob, toCsv } from '../../../utils/reportExportUtils';
import { sendReportEmail } from '../../../utils/reportEmailSend';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import practiceService from '../../../services/practiceService';
import {
  MEASURED_TYPE_IDS,
  normalizePracticeMonitoringTypes,
  PRACTICE_VITAL_ADD_DISABLED_TITLE
} from '../../../utils/practiceMonitoringUtils';
import { buildMeasurementAiSummaryPayload, readRecordedNumber } from '../../../utils/measurementAiSummaryPayload';
import {
  exportBloodPressureTablesPdf,
  exportBloodGlucoseTablesPdf,
  exportWeightTablesPdf
} from '../../../utils/measurementTablePdfExport';
import {
  getMeasurementReviewDisplay,
  isAbnormalVitalStatus,
} from '../../../utils/patientAbnormalReviewDisplay';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';

// --- ICONS ---
const HeartVitalIcon = ({ active }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={active ? "#e11d48" : "none"} stroke={active ? "#e11d48" : "#94a3b8"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.6z" />
  </svg>
);
const DropVitalIcon = ({ active }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={active ? "#0284c7" : "none"} stroke={active ? "#0284c7" : "#94a3b8"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2.5s7 8.2 7 12.7a7 7 0 1 1-14 0c0-4.5 7-12.7 7-12.7z" />
  </svg>
);
const ScaleVitalIcon = ({ active }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={active ? "#d97706" : "#94a3b8"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="3" x2="12" y2="21" /><path d="M5 7l-3 6a3 3 0 0 0 6 0z" /><path d="M19 7l-3 6a3 3 0 0 0 6 0z" />
    <path d="M5 7h14" /><path d="M8 21h8" />
  </svg>
);
const TableIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);
const ChartIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" /><line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);
const sameRowId = (a, b) => String(a ?? '') === String(b ?? '');

const formatMeasurementTimeLabel = (m) => {
  if (!m) return '';
  const timeVal = m.measurement_time || m.time_recorded || '';
  if (!timeVal) return '';
  try {
    const [hStr, mStr] = String(timeVal).split(':');
    let h = parseInt(hStr, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${String(mStr).padStart(2, '0')} ${ampm}`;
  } catch (e) {
    return String(timeVal);
  }
};

const expandPeriodSlotRows = (rowItem, columns) => {
  const keys = (columns || []).map((c) => c.key);
  const maxCount = Math.max(1, ...keys.map((k) => (rowItem?.periods?.[k] || []).length));
  return Array.from({ length: maxCount }, (_, i) => ({
    showDate: i === 0,
    slots: Object.fromEntries(keys.map((k) => [k, (rowItem?.periods?.[k] || [])[i] || null]))
  }));
};

const getAlertStatusColor = (status) => {
  if (status === 'high') return '#d32f2f';
  if (status === 'low') return '#f57c00';
  return '#15803d';
};

const ReadingReviewBadge = ({
  reading,
  isAbnormal,
  practiceId,
  patientId,
  vitalType,
  onReviewed,
  disabled = false,
  caregiverOptions = [],
  providerOptions = [],
}) => {
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserId = currentUser?.id || currentUser?.user_id || '';
  const [isSaving, setIsSaving] = useState(false);
  const [showAssignPopover, setShowAssignPopover] = useState(false);
  const [pickRole, setPickRole] = useState(() => {
    const roleId = Number(currentUser?.role_id);
    return roleId === 5 || roleId === 7 ? 'caregiver' : 'provider';
  });
  const [selectedId, setSelectedId] = useState('');
  const [isSavingAssign, setIsSavingAssign] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [assignmentPatch, setAssignmentPatch] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const triggerRef = useRef(null);

  const mergedReading = useMemo(
    () => (reading && assignmentPatch ? { ...reading, ...assignmentPatch } : reading),
    [reading, assignmentPatch]
  );

  useEffect(() => {
    if (!reading || !assignmentPatch) return;
    const parentAssigned = reading.assigned_to_user_id;
    const patchAssigned = assignmentPatch.assigned_to_user_id;
    const parentCaughtUp =
      (patchAssigned == null && (parentAssigned == null || parentAssigned === '')) ||
      (patchAssigned != null && sameRowId(parentAssigned, patchAssigned));
    const parentReviewed = Boolean(assignmentPatch.reviewed_at && reading.reviewed_at);
    if (parentCaughtUp || parentReviewed) {
      setAssignmentPatch(null);
    }
  }, [reading, assignmentPatch]);

  useLayoutEffect(() => {
    if (!showAssignPopover || !triggerRef.current) {
      setPopoverPos(null);
      return undefined;
    }
    const place = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = 240;
      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const preferAbove = rect.top > 240;
      setPopoverPos({
        width,
        left,
        ...(preferAbove
          ? { bottom: window.innerHeight - rect.top + 8, top: 'auto' }
          : { top: rect.bottom + 8, bottom: 'auto' })
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [showAssignPopover]);

  if (!isAbnormal || !mergedReading) return null;
  const review = getMeasurementReviewDisplay(mergedReading);
  const isSuperAdmin = Number(currentUser?.role_id) === 1;
  const assignOnly = Number(currentUser?.role_id) === 3;
  if (isSuperAdmin || (assignOnly && review)) {
    const reviewed = Boolean(review);
    const title = reviewed
      ? [review.by ? `Reviewed by ${review.by}` : 'Reviewed', review.role || '', review.atLabel || ''].filter(Boolean).join(' · ')
      : 'Pending';
    return (
      <span className={`prc-status-label ${reviewed ? 'is-reviewed' : 'is-pending'}`} title={title}>
        {reviewed ? 'Reviewed' : 'Pending'}
      </span>
    );
  }
  if (review) {
    const title = [
      review.by ? `Reviewed by ${review.by}` : 'Reviewed',
      review.role || '',
      review.atLabel || '',
    ].filter(Boolean).join(' · ');
    return (
      <span className="res-action-btn bphr-reviewed-icon-only" title={title}>
        <FontAwesomeIcon icon={faCheck} />
      </span>
    );
  }

  const assignedToId = mergedReading.assigned_to_user_id || null;
  const assignedToName = mergedReading.assigned_to_name || '';
  const assignedToRole = mergedReading.assigned_to_role || 'Staff';
  const assignedByName = mergedReading.assigned_by_name || '';
  const assignedByRole = mergedReading.assigned_by_role || 'Staff';

  const isAssigned = Boolean(assignedToId || assignedToName);
  const isAssignedToMe = Boolean(
    isAssigned && currentUserId && assignedToId && String(assignedToId) === String(currentUserId)
  );
  const isAssignedToOther = Boolean(
    isAssigned && (!isAssignedToMe)
  );
  const isAssignedByMe = Boolean(
    isAssigned && currentUserId && mergedReading.assigned_by_user_id && String(mergedReading.assigned_by_user_id) === String(currentUserId)
  );
  const canReassignOrUnassign = isAssigned && (isAssignedByMe || isAssignedToMe || assignOnly);

  const applyReadingPatch = (patch) => {
    setAssignmentPatch((prev) => ({ ...(prev || {}), ...patch }));
    if (typeof onReviewed === 'function') {
      onReviewed(mergedReading.id, patch);
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSaving || disabled || isAssignedToOther || !practiceId || !patientId || !vitalType || !mergedReading?.id) return;
    setIsSaving(true);
    try {
      const response = await patientService.reviewPatientMeasurement(
        practiceId,
        patientId,
        vitalType,
        mergedReading.id
      );
      if (response?.data?.success) {
        toast.success('Reading marked as reviewed');
        const nowIso = new Date().toISOString();
        const reviewerName =
          response.data.reviewed_by_name ||
          `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() ||
          currentUser?.name ||
          'You';
        const reviewerRole =
          response.data.reviewed_by_role ||
          currentUser?.role_name ||
          '';
        applyReadingPatch({
          reviewed_at: response.data.reviewed_at || nowIso,
          reviewed_by: response.data.reviewed_by || currentUser?.id,
          reviewed_by_name: reviewerName,
          reviewed_by_role: reviewerRole,
          assigned_to_user_id: null,
          assigned_to_name: null,
          assigned_to_role: null
        });
      }
    } catch (err) {
      console.error('Error reviewing measurement:', err);
      toast.error(err?.response?.data?.message || 'Failed to mark reading as reviewed');
    } finally {
      setIsSaving(false);
    }
  };

  const activeOptions = (() => {
    const roleId = Number(currentUser?.role_id);
    const selfMatchesRole =
      (pickRole === 'provider' && roleId === 4) ||
      (pickRole === 'caregiver' && (roleId === 5 || roleId === 7));
    const source = pickRole === 'provider' ? providerOptions : caregiverOptions;
    const isSelf = (opt) => currentUserId && String(opt.id || opt.user_id) === String(currentUserId);
    const others = (source || []).filter((opt) => {
      const optId = String(opt.id || opt.user_id);
      if (isSelf(opt)) return false;
      if (assignedToId && optId === String(assignedToId)) return false;
      return true;
    });
    const alreadyAssignedToMe = assignedToId && String(assignedToId) === String(currentUserId);
    if (!selfMatchesRole || !currentUserId || alreadyAssignedToMe) return others;
    const selfOpt = (source || []).find(isSelf) || {
      id: currentUserId,
      user_id: currentUserId,
      first_name: currentUser?.first_name,
      last_name: currentUser?.last_name,
      name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim()
    };
    return [selfOpt, ...others];
  })();

  const handleConfirmAssign = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSavingAssign || !selectedId || !practiceId || !patientId || !vitalType || !mergedReading?.id) return;
    setIsSavingAssign(true);
    try {
      const res = await patientService.assignMeasurement(
        practiceId,
        patientId,
        vitalType,
        mergedReading.id,
        {
          assigneeId: selectedId,
          role: pickRole,
          purpose: 'invite'
        }
      );
      if (res?.data?.success) {
        const selOpt = activeOptions.find((o) => String(o.id || o.user_id) === String(selectedId));
        const assigneeName = res.data.assigned_to_name || (selOpt ? `${selOpt.first_name || ''} ${selOpt.last_name || ''}`.trim() : `User #${selectedId}`);
        const assigneeRole = res.data.assigned_to_role || (pickRole === 'provider' ? 'Provider' : 'Caregiver');

        toast.success(res.data.message || (isAssigned ? 'Reading reassigned successfully' : 'Reading assigned successfully'));
        setShowAssignPopover(false);
        setSelectedId('');
        applyReadingPatch({
          assigned_to_user_id: res.data.assigned_to_user_id || selectedId,
          assigned_to_name: assigneeName,
          assigned_to_role: assigneeRole,
          assigned_by_user_id: res.data.assigned_by_user_id || currentUserId,
          assigned_by_name: res.data.assigned_by_name || `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'You',
          assigned_by_role: res.data.assigned_by_role || currentUser?.role_name || 'Staff',
          assigned_at: res.data.assigned_at || new Date().toISOString()
        });
      }
    } catch (err) {
      console.error('Error assigning reading:', err);
      toast.error(err?.response?.data?.message || 'Failed to assign reading');
    } finally {
      setIsSavingAssign(false);
    }
  };

  const handleUnassignReading = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUnassigning || !practiceId || !patientId || !vitalType || !mergedReading?.id) return;
    setIsUnassigning(true);
    try {
      const res = await patientService.unassignMeasurement(practiceId, patientId, vitalType, mergedReading.id);
      if (res?.data?.success) {
        toast.success('Reading assignment removed');
        setShowAssignPopover(false);
        setSelectedId('');
        applyReadingPatch({
          assigned_to_user_id: null,
          assigned_to_name: null,
          assigned_to_role: null,
          assigned_by_user_id: null,
          assigned_by_name: null,
          assigned_by_role: null,
          assigned_at: null
        });
      }
    } catch (err) {
      console.error('Error unassigning reading:', err);
      toast.error(err?.response?.data?.message || 'Failed to remove assignment');
    } finally {
      setIsUnassigning(false);
    }
  };

  let buttonTitle = "Click to mark this abnormal reading as reviewed";
  if (isAssignedToOther) {
    buttonTitle = `Assigned to ${assignedToName} (${assignedToRole}) for review. Only the assigned reviewer can review.`;
  } else if (isAssignedToMe) {
    buttonTitle = `Assigned to you by ${assignedByRole} ${assignedByName}. Click to review.`;
  }

  return (
    <div className="pd-reading-review-container" ref={triggerRef} onClick={(e) => e.stopPropagation()}>
      <div className="pd-reading-review-buttons-row">
        {isAssigned ? (
          <>
            {isAssignedToMe && !assignOnly ? (
              <button
                type="button"
                className="res-action-btn res-view-btn"
                title={`Assigned to you by ${assignedByRole} ${assignedByName || 'Staff'}. Click to review.`}
                onClick={handleReview}
                disabled={isSaving || disabled}
              >
                {isSaving ? '…' : <FontAwesomeIcon icon={faCheck} />}
              </button>
            ) : (
              <span
                className="res-action-btn res-view-btn"
                title={`Assigned to ${assignedToName} (${assignedToRole})${assignedByName ? ` by ${assignedByRole} ${assignedByName}` : ''}`}
                style={{ cursor: 'default' }}
              >
                <FontAwesomeIcon icon={faUserCheck} />
              </span>
            )}

            {!disabled && canReassignOrUnassign && (
              <button
                type="button"
                className="res-action-btn res-delete-btn"
                title="Remove assignment"
                onClick={handleUnassignReading}
                disabled={isUnassigning || disabled}
              >
                {isUnassigning ? '…' : <FontAwesomeIcon icon={faTimes} />}
              </button>
            )}
          </>
        ) : (
          <>
            {!assignOnly && (
              <button
                type="button"
                className="res-action-btn res-view-btn"
                title="Mark as reviewed"
                onClick={handleReview}
                disabled={isSaving || disabled}
              >
                {isSaving ? '…' : <FontAwesomeIcon icon={faCheck} />}
              </button>
            )}

            {!disabled && (
              <button
                type="button"
                className="res-action-btn res-view-btn"
                title="Assign reading to caregiver or provider"
                onClick={() => setShowAssignPopover((prev) => !prev)}
                disabled={isSaving || disabled}
              >
                <FontAwesomeIcon icon={faUserPlus} />
              </button>
            )}
          </>
        )}
      </div>

      {showAssignPopover && typeof document !== 'undefined'
        ? createPortal(
            <>
              <div
                className="pd-reading-assign-popover-backdrop"
                onClick={() => { setShowAssignPopover(false); setSelectedId(''); }}
              />
              <div
                className="pd-reading-assign-popover pd-reading-assign-popover--portal"
                style={popoverPos || { visibility: 'hidden' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="pd-assign-popover-title">{isAssigned ? 'Reassign Reading' : 'Assign Reading'}</div>
                <div className="prc-role-toggle">
                  <button
                    type="button"
                    className={`prc-role-btn ${pickRole === 'caregiver' ? 'is-active' : ''}`}
                    onClick={() => { setPickRole('caregiver'); setSelectedId(''); }}
                  >
                    Caregiver
                  </button>
                  <button
                    type="button"
                    className={`prc-role-btn ${pickRole === 'provider' ? 'is-active' : ''}`}
                    onClick={() => { setPickRole('provider'); setSelectedId(''); }}
                  >
                    Provider
                  </button>
                </div>

                <select
                  className="prc-select"
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  disabled={isSavingAssign || activeOptions.length === 0}
                >
                  <option value="">{activeOptions.length === 0 ? `No ${pickRole}s` : `Select ${pickRole}…`}</option>
                  {activeOptions.map((opt) => {
                    const optId = opt.id || opt.user_id;
                    const isMe = currentUserId && String(optId) === String(currentUserId);
                    const name = `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.name || `${pickRole} #${optId}`;
                    return (
                      <option key={optId} value={optId}>
                        {isMe ? `${name} (Me)` : name}
                      </option>
                    );
                  })}
                </select>

                <div className="pd-assign-popover-actions">
                  <button
                    type="button"
                    className="prc-assign-cancel"
                    onClick={() => { setShowAssignPopover(false); setSelectedId(''); }}
                    disabled={isSavingAssign}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="prc-assign-confirm"
                    onClick={handleConfirmAssign}
                    disabled={isSavingAssign || !selectedId}
                  >
                    {isSavingAssign ? 'Sending…' : 'Assign & email'}
                  </button>
                </div>
              </div>
            </>,
            document.body
          )
        : null}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label, chartType }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: "6px",
        padding: "8px 12px",
        fontSize: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
      }}>
        <div style={{ fontWeight: 700, marginBottom: "4px", color: "#1a3550" }}>
          {label}
        </div>
        {payload.map((entry, idx) => (
          <div key={idx} style={{ color: entry.color, display: "flex", gap: "6px", alignItems: "center" }}>
            <span style={{ width: "10px", height: "2px", background: entry.color, display: "inline-block" }}></span>
            <span>{entry.name}:</span>
            <span style={{ fontWeight: 600 }}>
              {entry.value}
              {chartType === "glucose" ? " mg/dL" : chartType === "weight" ? " lbs" : " mmHg"}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Period columns match dedicated measurement pages (hussain / production).
const BP_PERIOD_COLUMNS = [
  { key: 'WAKE-UP', label: 'Wake-up', range: '5:00 AM–7:59 AM' },
  { key: 'MORNING', label: 'Morning', range: '8:00 AM–10:59 AM' },
  { key: 'NOON', label: 'Noon', range: '11:00 AM–1:59 PM' },
  { key: 'AFTERNOON', label: 'Afternoon', range: '2:00 PM–4:59 PM' },
  { key: 'EVENING', label: 'Evening', range: '5:00 PM–4:59 AM' },
];

const BG_PERIOD_COLUMNS = [
  { key: 'WAKE-UP TIME', label: 'WAKE-UP TIME', range: '06:00 AM–06:59 AM' },
  { key: 'BEFORE-BREAKFAST', label: 'BEFORE-BREAKFAST', range: '07:00 AM–07:59 AM' },
  { key: 'AFTER-BREAKFAST', label: 'AFTER-BREAKFAST', range: '08:00 AM–10:59 AM' },
  { key: 'BEFORE-LUNCH', label: 'BEFORE-LUNCH', range: '11:00 AM–12:59 PM' },
  { key: 'AFTER-LUNCH', label: 'AFTER-LUNCH', range: '01:00 PM–04:59 PM' },
  { key: 'BEFORE-DINNER', label: 'BEFORE-DINNER', range: '05:00 PM–06:59 PM' },
  { key: 'AFTER-DINNER', label: 'AFTER-DINNER', range: '07:00 PM–08:29 PM' },
  { key: 'BEDTIME', label: 'BEDTIME', range: '08:30 PM–09:59 PM' },
  { key: 'MIDNIGHT', label: 'MIDNIGHT', range: '10:00 PM–05:59 AM' },
];

const WEIGHT_PERIOD_COLUMNS = [
  { key: 'MORNING', label: 'MORNING', range: '06:00 AM–11:59 AM' },
  { key: 'AFTERNOON', label: 'AFTERNOON', range: '12:00 PM–05:59 PM' },
  { key: 'EVENING', label: 'EVENING', range: '06:00 PM–05:59 AM' },
];

const readGlucoseValue = (m) => {
  if (!m) return null;
  const raw =
    m.blood_glucose_value_1 ??
    m.blood_glucose_value ??
    m.glucose_value ??
    m.glucose;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const readWeightValue = (m) => {
  if (!m) return null;
  if (m.weight_lbs != null && m.weight_lbs !== '' && !Number.isNaN(Number(m.weight_lbs))) {
    const lbs = Number(m.weight_lbs);
    return Number.isFinite(lbs) && lbs > 0 ? lbs : null;
  }
  const val = Number(m.weight);
  return Number.isFinite(val) && val > 0 ? val : null;
};

const RANGE_CHIPS = [
  { value: '7', label: '7d' },
  { value: '14', label: '14d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: 'All', label: 'All' },
];

const VITALS_CONFIG = [
  { key: "BLOOD PRESSURE", label: "Blood Pressure", color: "#1B2A4A", tint: "#f0f9ff", configKey: "bp" },
  { key: "BLOOD GLUCOSE", label: "Blood Glucose", color: "#1B2A4A", tint: "#f0f9ff", configKey: "bg" },
  { key: "WEIGHT", label: "Weight", color: "#1B2A4A", tint: "#f0f9ff", configKey: "wt" },
];

/**
 * RPM Overview tab — redesign with Vitals tabs, View switcher (Combined, Table, Chart), Query period & measurements.
 */
const PatientRpmOverview = ({
  practiceId,
  patientId,
  fromDashboard,
  activeProgram = 'RPM',
  navigate,
  isBPConfigured = true,
  isBGConfigured = true,
  isWeightConfigured = true,
  bp,
  bg,
  weight,
  bgDisplayValue,
  bpStatus,
  bgStatus,
  weightStatus,
  pulseStatus,
  getVitalDisplayDateTime,
  deviceSerial,
  patientData,
  scheduleTargets,
  onRefresh,
  onReviewStatusChange,
  isReadOnly = false,
  caregiverOptions = [],
  providerOptions = [],
  initialDateRange = '30',
  initialSelectedVitals = null,
}) => {
  const { user } = useSelector((state) => state.auth || {});
  const aiBasedAnalysisEnabled = isAiBasedAnalysisEnabled(user);
  const [internalCaregivers, setInternalCaregivers] = useState(caregiverOptions);
  const [internalProviders, setInternalProviders] = useState(providerOptions);

  useEffect(() => {
    if (Array.isArray(caregiverOptions) && caregiverOptions.length > 0) {
      setInternalCaregivers(caregiverOptions);
    } else if (practiceId) {
      practiceService.getPracticeCaregivers(practiceId).then((res) => {
        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        if (list.length > 0) setInternalCaregivers(list);
      }).catch(() => {});
    }
  }, [caregiverOptions, practiceId]);

  useEffect(() => {
    if (Array.isArray(providerOptions) && providerOptions.length > 0) {
      setInternalProviders(providerOptions);
    } else if (practiceId) {
      practiceService.getPracticeProviders(practiceId).then((res) => {
        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        if (list.length > 0) setInternalProviders(list);
      }).catch(() => {});
    }
  }, [providerOptions, practiceId]);

  const handleReadingReviewed = useCallback((readingId, updatedFields) => {
    setMeasurements((prev) =>
      Array.isArray(prev)
        ? prev.map((m) => (sameRowId(m.id, readingId) ? { ...m, ...updatedFields } : m))
        : prev
    );
    if (typeof onReviewStatusChange === 'function' && updatedFields) {
      const assignedTo = updatedFields.assigned_to_user_id;
      onReviewStatusChange(patientId, {
        ...(updatedFields.reviewed_at
          ? {
              is_reviewed: true,
              last_reviewed_at: updatedFields.reviewed_at,
              last_reviewed_by: updatedFields.reviewed_by,
              last_reviewed_by_name: updatedFields.reviewed_by_name,
              last_reviewed_by_role: updatedFields.reviewed_by_role
            }
          : {}),
        abnormal_assignee_id: assignedTo ?? null,
        abnormal_assigned_to_user_id: assignedTo ?? null,
        abnormal_assignee_name: updatedFields.assigned_to_name ?? null,
        abnormal_assignee_role: updatedFields.assigned_to_role ?? null,
        abnormal_assigned_by_user_id: updatedFields.assigned_by_user_id ?? null,
        abnormal_assigned_by_name: updatedFields.assigned_by_name ?? null,
        abnormal_assigned_by_role: updatedFields.assigned_by_role ?? null,
        abnormal_assigned_at: updatedFields.assigned_at ?? null
      });
    }
    if (updatedFields?.reviewed_at && typeof onRefresh === 'function') {
      onRefresh();
    }
  }, [onRefresh, onReviewStatusChange, patientId]);

  const vitalEnabled = useMemo(
    () => ({
      bp: Boolean(isBPConfigured),
      bg: Boolean(isBGConfigured),
      wt: Boolean(isWeightConfigured),
    }),
    [isBPConfigured, isBGConfigured, isWeightConfigured]
  );

  const practiceVitalEnabled = useMemo(() => {
    const raw = patientData?.practice_monitoring_types;
    if (!Array.isArray(raw)) {
      return { bp: true, bg: true, wt: true };
    }
    const types = normalizePracticeMonitoringTypes(raw);
    return {
      bp: types.includes(MEASURED_TYPE_IDS.BP),
      bg: types.includes(MEASURED_TYPE_IDS.BG),
      wt: types.includes(MEASURED_TYPE_IDS.WT),
    };
  }, [patientData?.practice_monitoring_types]);

  const canAddVital = useMemo(
    () => ({
      bp: vitalEnabled.bp && practiceVitalEnabled.bp,
      bg: vitalEnabled.bg && practiceVitalEnabled.bg,
      wt: vitalEnabled.wt && practiceVitalEnabled.wt,
    }),
    [vitalEnabled, practiceVitalEnabled]
  );

  const firstEnabledVital = useMemo(() => {
    const match = VITALS_CONFIG.find((v) => vitalEnabled[v.configKey]);
    return match?.key || null;
  }, [vitalEnabled]);

  const [selectedVitals, setSelectedVitals] = useState(
    () => initialSelectedVitals || firstEnabledVital || "BLOOD PRESSURE"
  );
  const [viewMode, setViewMode] = useState("combined"); // 'combined', 'table', 'chart'
  const [dateMode, setDateMode] = useState("range");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [dateRange, setDateRange] = useState(() => String(initialDateRange || "30"));
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const todayIso = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);
  const [sortBy, setSortBy] = useState("Weekly average");
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(true);
  const [hoverReadings, setHoverReadings] = useState(null); // { x, y, readings[] }

  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState('');
  const aiRequestIdRef = useRef(0);

  const isGlucose = selectedVitals.includes("GLUCOSE");
  const isWeight = selectedVitals.includes("WEIGHT");
  const isSelectedVitalEnabled =
    (selectedVitals.includes("GLUCOSE") && vitalEnabled.bg) ||
    (selectedVitals.includes("WEIGHT") && vitalEnabled.wt) ||
    (!selectedVitals.includes("GLUCOSE") &&
      !selectedVitals.includes("WEIGHT") &&
      vitalEnabled.bp);
  const isSelectedVitalAddable =
    !isReadOnly &&
    ((selectedVitals.includes("GLUCOSE") && canAddVital.bg) ||
    (selectedVitals.includes("WEIGHT") && canAddVital.wt) ||
    (!selectedVitals.includes("GLUCOSE") &&
      !selectedVitals.includes("WEIGHT") &&
      canAddVital.bp));

  // When enrollment loads/changes, keep selection on an enrolled vital.
  useEffect(() => {
    const selectedConfig = VITALS_CONFIG.find((v) => v.key === selectedVitals);
    const selectedEnabled = selectedConfig
      ? vitalEnabled[selectedConfig.configKey]
      : false;
    if (!selectedEnabled && firstEnabledVital) {
      setSelectedVitals(firstEnabledVital);
      setShowAddModal(false);
    }
  }, [selectedVitals, vitalEnabled, firstEnabledVital]);

  useEffect(() => {
    if (!isSelectedVitalAddable) setShowAddModal(false);
  }, [isSelectedVitalAddable]);

  const fetchMeasurements = useCallback(async () => {
    if (!practiceId || !patientId) return;

    const selectedConfig = VITALS_CONFIG.find((v) => v.key === selectedVitals);
    if (!selectedConfig || !vitalEnabled[selectedConfig.configKey]) {
      setMeasurements([]);
      return;
    }

    try {
      setLoadingMeasurements(true);
      let fromDateParam = fromDate;
      let toDateParam = toDate;

      if (dateMode === 'range' && String(dateRange).toLowerCase() !== 'all') {
        const days = parseInt(String(dateRange).replace(/\D/g, ''), 10) || 30;
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - days);
        const formatLocalYmd = (d) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        fromDateParam = formatLocalYmd(start);
        toDateParam = formatLocalYmd(end);
      } else if (dateMode === 'range' && String(dateRange).toLowerCase() === 'all') {
        fromDateParam = undefined;
        toDateParam = undefined;
      }

      let response;
      if (selectedVitals.includes('GLUCOSE')) {
        response = await measurementService.getBloodGlucoseByDateRange(
          practiceId, patientId, fromDateParam, toDateParam
        );
      } else if (selectedVitals.includes('WEIGHT')) {
        response = await measurementService.getWeightByDateRange(
          practiceId, patientId, fromDateParam, toDateParam
        );
      } else {
        response = await measurementService.getBloodPressureByDateRange(
          practiceId, patientId, fromDateParam, toDateParam
        );
      }

      if (response?.data?.success) {
        setMeasurements(response.data.data || []);
      } else if (Array.isArray(response?.data?.data)) {
        setMeasurements(response.data.data);
      } else if (Array.isArray(response?.data)) {
        setMeasurements(response.data);
      } else {
        setMeasurements([]);
      }
    } catch (err) {
      console.error('Error fetching measurements for RPM Overview:', err);
      setMeasurements([]);
    } finally {
      setLoadingMeasurements(false);
    }
  }, [practiceId, patientId, selectedVitals, dateMode, dateRange, fromDate, toDate, vitalEnabled]);

  useEffect(() => {
    fetchMeasurements();
  }, [fetchMeasurements]);

  useEffect(() => {
    const handleMeasurementAdded = (event) => {
      const evtPracticeId = String(event?.detail?.practiceId || '');
      const evtPatientId = String(event?.detail?.patientId || '');
      if (evtPracticeId && evtPatientId && (evtPracticeId !== String(practiceId) || evtPatientId !== String(patientId))) {
        return;
      }
      fetchMeasurements();
      if (typeof onRefresh === 'function') onRefresh();
    };
    window.addEventListener('bp:measurement-added', handleMeasurementAdded);
    window.addEventListener('bg:measurement-added', handleMeasurementAdded);
    window.addEventListener('weight:measurement-added', handleMeasurementAdded);
    window.addEventListener('measurement:added', handleMeasurementAdded);
    return () => {
      window.removeEventListener('bp:measurement-added', handleMeasurementAdded);
      window.removeEventListener('bg:measurement-added', handleMeasurementAdded);
      window.removeEventListener('weight:measurement-added', handleMeasurementAdded);
      window.removeEventListener('measurement:added', handleMeasurementAdded);
    };
  }, [practiceId, patientId, fetchMeasurements, onRefresh]);

  const periodColumns = useMemo(() => {
    if (isGlucose) return BG_PERIOD_COLUMNS;
    if (isWeight) return WEIGHT_PERIOD_COLUMNS;
    return BP_PERIOD_COLUMNS;
  }, [isGlucose, isWeight]);

  const activeChartData = useMemo(() => {
    if (!measurements || measurements.length === 0) return [];

    const sorted = [...measurements].sort((a, b) => {
      const dA = a.measurement_date || a.date_recorded || a.created_at || a.date || '';
      const dB = b.measurement_date || b.date_recorded || b.created_at || b.date || '';
      const tA = a.measurement_time || a.time_recorded || '00:00:00';
      const tB = b.measurement_time || b.time_recorded || '00:00:00';
      const fullA = `${String(dA).split('T')[0]} ${tA}`;
      const fullB = `${String(dB).split('T')[0]} ${tB}`;
      return fullA.localeCompare(fullB);
    });

    return sorted
      .map((m) => {
        const rawDate = m.measurement_date || m.date_recorded || m.created_at || m.date || '';
        const rawTime = m.measurement_time || m.time_recorded || '';
        let dateLabel = rawDate;
        if (rawDate && String(rawDate).includes('-')) {
          const parts = String(rawDate).split('T')[0].split('-');
          if (parts.length === 3) {
            dateLabel = `${parts[1]}/${parts[2]}`;
          }
        }
        let timeLabel = '';
        if (rawTime) {
          try {
            const [hStr, mStr] = String(rawTime).split(':');
            let h = parseInt(hStr, 10);
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12 || 12;
            timeLabel = ` ${h}:${mStr.padStart(2, '0')}${ampm}`;
          } catch (e) {
            timeLabel = ` ${rawTime}`;
          }
        }
        const fullLabel = `${dateLabel}${timeLabel}`;

        if (isGlucose) {
          const glucose = readGlucoseValue(m);
          if (glucose == null || Number.isNaN(glucose) || glucose <= 0) return null;
          return { date: fullLabel, rawDate: dateLabel, glucose };
        }
        if (isWeight) {
          const weightVal = readWeightValue(m);
          if (weightVal == null || Number.isNaN(weightVal) || weightVal <= 0) return null;
          return { date: fullLabel, rawDate: dateLabel, weight: weightVal };
        }
        const sys = Number(m.systolic_pressure ?? m.systolic);
        const dia = Number(m.diastolic_pressure ?? m.diastolic);
        if (Number.isNaN(sys) || Number.isNaN(dia) || sys <= 0 || dia <= 0) return null;
        return {
          date: fullLabel,
          rawDate: dateLabel,
          systolic: sys,
          diastolic: dia
        };
      })
      .filter(Boolean);
  }, [measurements, isGlucose, isWeight]);

  const combinedWeightTrendData = useMemo(() => {
    if (!isWeight || !measurements?.length) return [];

    const daily = {};
    measurements.forEach((measurement) => {
      const rawDate =
        measurement.measurement_date ||
        measurement.date_recorded ||
        measurement.measure_new_date_time?.split('T')[0] ||
        measurement.created_at?.split('T')[0] ||
        measurement.date;
      if (!rawDate) return;

      const date = String(rawDate).split('T')[0];
      if (!daily[date]) daily[date] = { weight: [], fat: [], bmi: [] };

      const weight = readWeightValue(measurement);
      const fat = Number(measurement.fat ?? measurement.body_fat);
      const bmi = Number(measurement.bmi);
      if (weight != null) daily[date].weight.push(weight);
      if (Number.isFinite(fat) && fat > 0) daily[date].fat.push(fat);
      if (Number.isFinite(bmi) && bmi > 0) daily[date].bmi.push(bmi);
    });

    const average = (values) =>
      values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

    return Object.keys(daily)
      .sort((a, b) => a.localeCompare(b))
      .map((date) => {
        const [year, month, day] = date.split('-');
        return {
          date: month && day ? `${month}/${day}/${year}` : date,
          weight: average(daily[date].weight),
          fat: average(daily[date].fat),
          bmi: average(daily[date].bmi)
        };
      });
  }, [isWeight, measurements]);

  const mapBpOrWeightPeriod = (m) => {
    const periodName = String(m.period_name || m.period || '').toUpperCase();
    if (isWeight) {
      if (periodName.includes('MORN')) return 'MORNING';
      if (periodName.includes('AFTER')) return 'AFTERNOON';
      if (periodName.includes('EVEN')) return 'EVENING';
      const timeStr = m.measurement_time || m.time_recorded || '';
      if (timeStr) {
        const hour = parseInt(String(timeStr).split(':')[0], 10);
        if (!Number.isNaN(hour)) {
          if (hour >= 6 && hour < 12) return 'MORNING';
          if (hour >= 12 && hour < 18) return 'AFTERNOON';
          return 'EVENING';
        }
      }
      return 'MORNING';
    }

    if (periodName.includes('WAKE')) return 'WAKE-UP';
    if (periodName.includes('MORN')) return 'MORNING';
    if (periodName.includes('NOON') && !periodName.includes('AFTER')) return 'NOON';
    if (periodName.includes('AFTER')) return 'AFTERNOON';
    if (periodName.includes('EVEN')) return 'EVENING';
    const timeStr = m.measurement_time || m.time_recorded || '';
    if (timeStr) {
      const hour = parseInt(String(timeStr).split(':')[0], 10);
      if (!Number.isNaN(hour)) {
        if (hour >= 5 && hour < 8) return 'WAKE-UP';
        if (hour >= 8 && hour < 11) return 'MORNING';
        if (hour >= 11 && hour < 14) return 'NOON';
        if (hour >= 14 && hour < 17) return 'AFTERNOON';
        return 'EVENING';
      }
    }
    return 'AFTERNOON';
  };

  const mapBgPeriod = (m) => {
    const raw = String(
      m.period_name || m.period || m.measure_note || m.note || ''
    )
      .toUpperCase()
      .replace(/[_/]+/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
    const compact = raw.replace(/[\s-]+/g, '');

    if (compact.includes('WAKE')) return 'WAKE-UP TIME';
    if (compact.includes('BEFOREBREAKFAST') || compact === 'BEFOREMEAL') return 'BEFORE-BREAKFAST';
    if (compact.includes('AFTERBREAKFAST') || compact === 'AFTERMEAL') return 'AFTER-BREAKFAST';
    if (compact.includes('BEFORELUNCH')) return 'BEFORE-LUNCH';
    if (compact.includes('AFTERLUNCH')) return 'AFTER-LUNCH';
    if (compact.includes('BEFOREDINNER')) return 'BEFORE-DINNER';
    if (compact.includes('AFTERDINNER')) return 'AFTER-DINNER';
    if (compact.includes('BEDTIME') || compact.includes('BED')) return 'BEDTIME';
    if (compact.includes('MIDNIGHT') || compact.includes('NIGHT')) return 'MIDNIGHT';
    if (compact.includes('BEFORE')) return 'BEFORE-BREAKFAST';
    if (compact.includes('AFTER')) return 'AFTER-BREAKFAST';

    const timeStr = m.measurement_time || m.time_recorded || '';
    if (timeStr) {
      const parts = String(timeStr).split(':');
      const hour = parseInt(parts[0], 10);
      const min = parseInt(parts[1] || '0', 10);
      const decimalHour = hour + min / 60;

      if (!Number.isNaN(decimalHour)) {
        if (decimalHour >= 6 && decimalHour < 7) return 'WAKE-UP TIME';
        if (decimalHour >= 7 && decimalHour < 8) return 'BEFORE-BREAKFAST';
        if (decimalHour >= 8 && decimalHour < 11) return 'AFTER-BREAKFAST';
        if (decimalHour >= 11 && decimalHour < 13) return 'BEFORE-LUNCH';
        if (decimalHour >= 13 && decimalHour < 17) return 'AFTER-LUNCH';
        if (decimalHour >= 17 && decimalHour < 19) return 'BEFORE-DINNER';
        if (decimalHour >= 19 && decimalHour < 20.5) return 'AFTER-DINNER';
        if (decimalHour >= 20.5 && decimalHour < 22) return 'BEDTIME';
        return 'MIDNIGHT';
      }
    }
    return 'WAKE-UP TIME';
  };

  const activeTableRows = useMemo(() => {
    if (!measurements || measurements.length === 0) return null;

    const groups = {};
    measurements.forEach((m) => {
      const rawDate = m.measurement_date || m.date_recorded || m.created_at || m.date || '';
      const dateKey = rawDate ? String(rawDate).split('T')[0] : 'Unknown';
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(m);
    });

    const datesDesc = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    const periodKeys = periodColumns.map((c) => c.key);

    return datesDesc.map((dateKey) => {
      const dayReadings = groups[dateKey];
      const periodMap = Object.fromEntries(periodKeys.map((k) => [k, []]));

      dayReadings.forEach((m) => {
        const bucket = isGlucose ? mapBgPeriod(m) : mapBpOrWeightPeriod(m);
        if (!periodMap[bucket]) periodMap[bucket] = [];
        periodMap[bucket].push(m);
      });

      let dateDisplay = dateKey;
      if (dateKey.includes('-')) {
        const parts = dateKey.split('-');
        if (parts.length === 3) dateDisplay = `${parts[1]}-${parts[2]}-${parts[0]}`;
      }

      return {
        dateKey,
        dateDisplay,
        periods: periodMap
      };
    });
  }, [measurements, isGlucose, periodColumns]);

  const activeSummaryRows = useMemo(() => {
    if (!measurements || measurements.length === 0) return [];

    const periodLabel =
      dateMode === 'range'
        ? dateRange === 'All' || dateRange === 'all'
          ? 'All dates'
          : `Last ${dateRange} days`
        : `From ${fromDate || 'Start'} to ${toDate || 'End'}`;

    if (isGlucose) {
      const glucoseVals = measurements
        .map((m) => readGlucoseValue(m))
        .filter((v) => v != null);
      if (!glucoseVals.length) return [];
      const avg = Math.round(glucoseVals.reduce((a, b) => a + b, 0) / glucoseVals.length);
      const max = Math.max(...glucoseVals);
      const min = Math.min(...glucoseVals);
      return [{
        period: periodLabel,
        primaryAvg: avg,
        primaryMax: max,
        primaryMin: min,
        targetRange: formatBloodGlucoseTargetLabel(getPrimaryBloodGlucoseTarget(scheduleTargets)),
        kind: 'glucose'
      }];
    }

    if (isWeight) {
      const weightVals = measurements
        .map((m) => readWeightValue(m))
        .filter((v) => v != null);
      if (!weightVals.length) return [];
      const avg = Math.round(weightVals.reduce((a, b) => a + b, 0) / weightVals.length);
      const max = Math.max(...weightVals);
      const min = Math.min(...weightVals);
      return [{
        period: periodLabel,
        primaryAvg: avg,
        primaryMax: max,
        primaryMin: min,
        targetRange: formatWeightTargetLabel(scheduleTargets?.weightTargetRange),
        kind: 'weight'
      }];
    }

    const sysVals = measurements.map((m) => readRecordedNumber(m.systolic_pressure, m.systolic)).filter((v) => v != null);
    const diaVals = measurements.map((m) => readRecordedNumber(m.diastolic_pressure, m.diastolic)).filter((v) => v != null);
    const pulseVals = measurements.map((m) => readRecordedNumber(m.pulse)).filter((v) => v != null);

    if (!sysVals.length && !diaVals.length) return [];

    return [{
      period: periodLabel,
      systolicAvg: sysVals.length ? Math.round(sysVals.reduce((a, b) => a + b, 0) / sysVals.length) : '—',
      systolicMax: sysVals.length ? Math.max(...sysVals) : '—',
      systolicMin: sysVals.length ? Math.min(...sysVals) : '—',
      diastolicAvg: diaVals.length ? Math.round(diaVals.reduce((a, b) => a + b, 0) / diaVals.length) : '—',
      diastolicMax: diaVals.length ? Math.max(...diaVals) : '—',
      diastolicMin: diaVals.length ? Math.min(...diaVals) : '—',
      pulseAvg: pulseVals.length ? Math.round(pulseVals.reduce((a, b) => a + b, 0) / pulseVals.length) : '—',
      pulseMax: pulseVals.length ? Math.max(...pulseVals) : '—',
      pulseMin: pulseVals.length ? Math.min(...pulseVals) : '—',
      targetRange: formatBloodPressureTargetLabel(getPrimaryBloodPressureTarget(scheduleTargets)),
      kind: 'bp'
    }];
  }, [measurements, isGlucose, isWeight, dateMode, dateRange, fromDate, toDate, scheduleTargets]);

  const summaryPeriodData = useMemo(() => {
    const targetBounds = getAiTargetBounds(scheduleTargets);

    if (isGlucose) {
      const fallbackGlucosePeriods = [
        'Wake-up Time',
        'Before-Breakfast',
        'After-Breakfast',
        'Before-Lunch',
        'After-Lunch',
        'Before-Dinner',
        'After-Dinner',
        'Bedtime',
        'Midnight'
      ];
      const fallbackGlucoseRanges = {
        'Wake-up Time': '06:00 AM–06:59 AM',
        'Before-Breakfast': '07:00 AM–07:59 AM',
        'After-Breakfast': '08:00 AM–10:59 AM',
        'Before-Lunch': '11:00 AM–12:59 PM',
        'After-Lunch': '01:00 PM–04:59 PM',
        'Before-Dinner': '05:00 PM–06:59 PM',
        'After-Dinner': '07:00 PM–08:29 PM',
        'Bedtime': '08:30 PM–09:59 PM',
        'Midnight': '10:00 PM–05:59 AM'
      };
      const glucosePeriods = Array.isArray(scheduleTargets?.bloodGlucoseTimePeriod)
        ? scheduleTargets.bloodGlucoseTimePeriod
        : [];
      const { periodList, periodTimeRanges } = buildPeriodWindowsFromSchedule(
        glucosePeriods,
        fallbackGlucosePeriods,
        fallbackGlucoseRanges
      );

      const getGlucosePeriodKey = (m) => {
        const fromSchedule = matchMeasurementToPeriod(m, glucosePeriods, '');
        if (fromSchedule) return fromSchedule;
        const time = m.measurement_time || m.time_recorded || '';
        if (time) {
          const parts = String(time).split(':');
          const h = parseInt(parts[0], 10);
          const min = parseInt(parts[1] || '0', 10);
          if (!isNaN(h) && !isNaN(min)) {
            const totalMins = h * 60 + min;
            if (totalMins >= 360 && totalMins <= 419) return 'Wake-up Time';
            if (totalMins >= 420 && totalMins <= 479) return 'Before-Breakfast';
            if (totalMins >= 480 && totalMins <= 659) return 'After-Breakfast';
            if (totalMins >= 660 && totalMins <= 779) return 'Before-Lunch';
            if (totalMins >= 780 && totalMins <= 1019) return 'After-Lunch';
            if (totalMins >= 1020 && totalMins <= 1139) return 'Before-Dinner';
            if (totalMins >= 1140 && totalMins <= 1229) return 'After-Dinner';
            if (totalMins >= 1230 && totalMins <= 1319) return 'Bedtime';
            if (totalMins >= 1320 || totalMins <= 359) return 'Midnight';
          }
        }
        const raw = String(m.period_name || m.period || '').toUpperCase().replace(/[\s\-_]/g, '');
        if (raw.includes('WAKE')) return 'Wake-up Time';
        if (raw.includes('BEFOREBREAKFAST') || raw === 'BEFOREMEAL') return 'Before-Breakfast';
        if (raw.includes('AFTERBREAKFAST') || raw === 'AFTERMEAL') return 'After-Breakfast';
        if (raw.includes('BEFORELUNCH')) return 'Before-Lunch';
        if (raw.includes('AFTERLUNCH')) return 'After-Lunch';
        if (raw.includes('BEFOREDINNER')) return 'Before-Dinner';
        if (raw.includes('AFTERDINNER')) return 'After-Dinner';
        if (raw.includes('BEDTIME') || raw.includes('BED')) return 'Bedtime';
        if (raw.includes('MIDNIGHT') || raw.includes('NIGHT')) return 'Midnight';
        if (raw.includes('BEFORE')) return 'Before-Breakfast';
        if (raw.includes('AFTER')) return 'After-Breakfast';
        return periodList[0] || 'Wake-up Time';
      };

      const totals = { above: 0, within: 0, below: 0, total: 0 };
      const periodStats = {};
      periodList.forEach((pName) => {
        periodStats[pName] = { vals: [], count: 0, above: 0, within: 0, below: 0 };
      });

      (measurements || []).forEach((m) => {
        const pKey = getGlucosePeriodKey(m);
        const val = readGlucoseValue(m);
        if (val != null && periodStats[pKey]) {
          periodStats[pKey].vals.push(val);
          periodStats[pKey].count++;
          totals.total++;
          const bgTarget = getBloodGlucoseTargetForMeasurement(m, scheduleTargets);
          const status = checkBGValue(val, bgTarget);

          if (status === 'high') {
            periodStats[pKey].above++;
            totals.above++;
          } else if (status === 'low') {
            periodStats[pKey].below++;
            totals.below++;
          } else {
            periodStats[pKey].within++;
            totals.within++;
          }
        }
      });

      return { kind: 'glucose', periodList, periodTimeRanges, periodStats, totals };
    }

    if (isWeight) {
      const fallbackWeightPeriods = ['Morning', 'Afternoon', 'Evening'];
      const fallbackWeightRanges = {
        'Morning': '07:00 AM–11:59 AM',
        'Afternoon': '12:00 PM–05:59 PM',
        'Evening': '06:00 PM–06:59 AM'
      };
      const weightPeriods = Array.isArray(scheduleTargets?.weightTimePeriod)
        ? scheduleTargets.weightTimePeriod
        : [];
      const { periodList, periodTimeRanges } = buildPeriodWindowsFromSchedule(
        weightPeriods,
        fallbackWeightPeriods,
        fallbackWeightRanges
      );

      const getWeightPeriodKey = (m) => {
        const fromSchedule = matchMeasurementToPeriod(m, weightPeriods, '');
        if (fromSchedule) return fromSchedule;
        const raw = String(m.period_name || m.period || '').toLowerCase();
        if (raw.includes('morn')) return 'Morning';
        if (raw.includes('after')) return 'Afternoon';
        if (raw.includes('even')) return 'Evening';
        const time = m.measurement_time || m.time_recorded || '';
        if (time) {
          const hour = parseInt(String(time).split(':')[0], 10);
          if (hour >= 7 && hour < 12) return 'Morning';
          if (hour >= 12 && hour < 18) return 'Afternoon';
          return 'Evening';
        }
        return periodList[0] || 'Morning';
      };

      const totals = {
        aboveWeight: 0, withinWeight: 0, belowWeight: 0, totalWeight: 0,
        aboveFat: 0, withinFat: 0, belowFat: 0, totalFat: 0,
        aboveBmi: 0, withinBmi: 0, belowBmi: 0, totalBmi: 0,
      };
      const periodStats = {};
      periodList.forEach((pName) => {
        periodStats[pName] = { weightVals: [], fatVals: [], bmiVals: [], count: 0, above: 0, within: 0, below: 0 };
      });

      (measurements || []).forEach((m) => {
        const pKey = getWeightPeriodKey(m);
        const wVal = readWeightValue(m);
        const fVal = Number(m.fat ?? m.body_fat);
        const bVal = Number(m.bmi);

        if (wVal != null && periodStats[pKey]) {
          periodStats[pKey].weightVals.push(wVal);
          periodStats[pKey].count++;

          totals.totalWeight++;
          const wTarget = (scheduleTargets?.weightTargetRange && Object.keys(scheduleTargets.weightTargetRange).length > 0)
            ? scheduleTargets.weightTargetRange
            : { weightMin: targetBounds.weightMin, weightMax: targetBounds.weightMax };
          const wStatus = checkWeightValue(wVal, wTarget);

          if (wStatus === 'high') {
            periodStats[pKey].above++;
            totals.aboveWeight++;
          } else if (wStatus === 'low') {
            periodStats[pKey].below++;
            totals.belowWeight++;
          } else {
            periodStats[pKey].within++;
            totals.withinWeight++;
          }

          if (!isNaN(fVal) && fVal > 0) {
            periodStats[pKey].fatVals.push(fVal);
            totals.totalFat++;
            if (fVal > targetBounds.bodyFatMax) {
              totals.aboveFat++;
            } else if (fVal < targetBounds.bodyFatMin) {
              totals.belowFat++;
            } else {
              totals.withinFat++;
            }
          }

          if (!isNaN(bVal) && bVal > 0) {
            periodStats[pKey].bmiVals.push(bVal);
            totals.totalBmi++;
            if (bVal >= targetBounds.bmiOverweight) {
              totals.aboveBmi++;
            } else if (bVal < targetBounds.bmiNormal) {
              totals.belowBmi++;
            } else {
              totals.withinBmi++;
            }
          }
        }
      });

      return { kind: 'weight', periodList, periodTimeRanges, periodStats, totals };
    }

    // Default: Blood Pressure
    const fallbackBpPeriods = ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];
    const fallbackBpRanges = {
      'Wake-Up': '05:00 AM–07:59 AM',
      'Morning': '08:00 AM–10:59 AM',
      'Noon': '11:00 AM–01:59 PM',
      'Afternoon': '02:00 PM–04:59 PM',
      'Evening': '05:00 PM–04:59 AM'
    };
    const bpPeriods = Array.isArray(scheduleTargets?.bloodPressureTimePeriod)
      ? scheduleTargets.bloodPressureTimePeriod
      : [];
    const { periodList, periodTimeRanges } = buildPeriodWindowsFromSchedule(
      bpPeriods,
      fallbackBpPeriods,
      fallbackBpRanges
    );

    const getPeriodKey = (m) => {
      const fromSchedule = matchMeasurementToPeriod(m, bpPeriods, '');
      if (fromSchedule) return fromSchedule;
      const time = m.measurement_time || m.time_recorded || '';
      if (time) {
        const parts = String(time).split(':');
        const h = parseInt(parts[0], 10);
        const min = parseInt(parts[1] || '0', 10);
        if (!isNaN(h) && !isNaN(min)) {
          const totalMins = h * 60 + min;
          if (totalMins >= 300 && totalMins <= 479) return 'Wake-Up';
          if (totalMins >= 480 && totalMins <= 659) return 'Morning';
          if (totalMins >= 660 && totalMins <= 839) return 'Noon';
          if (totalMins >= 840 && totalMins <= 1019) return 'Afternoon';
          if (totalMins >= 1020 || totalMins <= 299) return 'Evening';
        }
      }
      const raw = String(m.period_name || m.period || '').toLowerCase();
      if (raw.includes('wake')) return 'Wake-Up';
      if (raw.includes('morn')) return 'Morning';
      if (raw.includes('noon')) return 'Noon';
      if (raw.includes('after')) return 'Afternoon';
      if (raw.includes('even')) return 'Evening';
      return periodList[0] || 'Wake-Up';
    };

    const totals = {
      above: { sys: 0, dia: 0 },
      within: { sys: 0, dia: 0 },
      below: { sys: 0, dia: 0 },
      sysTotal: 0,
      diaTotal: 0
    };

    const periodStats = {};
    periodList.forEach((pName) => {
      periodStats[pName] = {
        sysVals: [],
        diaVals: [],
        pulseVals: [],
        above: 0,
        within: 0,
        below: 0
      };
    });

    (measurements || []).forEach((m) => {
      const pKey = getPeriodKey(m);
      const sys = readRecordedNumber(m.systolic_pressure, m.systolic);
      const dia = readRecordedNumber(m.diastolic_pressure, m.diastolic);
      const pulse = readRecordedNumber(m.pulse);

      if (periodStats[pKey]) {
        const bpTarget = getBloodPressureTargetForMeasurement(m, scheduleTargets);
        const bpCheck = checkBPValues(m, bpTarget);
        const readingStatus = (value, reported) => {
          if (value == null) return null;
          if (value <= 0) return 'low';
          return reported === 'high' || reported === 'low' ? reported : 'normal';
        };
        const sysStatus = readingStatus(sys, bpCheck.sysStatus);
        const diaStatus = readingStatus(dia, bpCheck.diaStatus);

        if (sys != null) {
          periodStats[pKey].sysVals.push(sys);
          totals.sysTotal++;
          if (sysStatus === 'high') totals.above.sys++;
          else if (sysStatus === 'low') totals.below.sys++;
          else totals.within.sys++;
        }
        if (dia != null) {
          periodStats[pKey].diaVals.push(dia);
          totals.diaTotal++;
          if (diaStatus === 'high') totals.above.dia++;
          else if (diaStatus === 'low') totals.below.dia++;
          else totals.within.dia++;
        }
        if (pulse != null) {
          periodStats[pKey].pulseVals.push(pulse);
        }

        if (sys != null || dia != null) {
          if (sysStatus === 'high' || diaStatus === 'high') {
            periodStats[pKey].above++;
          } else if (sysStatus === 'low' || diaStatus === 'low') {
            periodStats[pKey].below++;
          } else {
            periodStats[pKey].within++;
          }
        }
      }
    });

    return { kind: 'bp', periodStats, totals, periodList, periodTimeRanges };
  }, [measurements, isGlucose, isWeight, scheduleTargets]);

  const formatPercentCount = (count, total) => {
    if (!total || total === 0) return '0% (0)';
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const summaryPeriodLabel = dateMode === 'range'
    ? (dateRange === 'All' || dateRange === 'all' ? 'All dates' : `Last ${dateRange} days`)
    : `From ${fromDate || 'Start'} to ${toDate || 'End'}`;

  const aiSummaryPayload = useMemo(
    () => buildMeasurementAiSummaryPayload({
      vitalType: isGlucose ? 'Blood Glucose' : isWeight ? 'Weight' : 'Blood Pressure',
      periodLabel: summaryPeriodLabel,
      measurements,
      summaryPeriodData,
      activeSummaryRows,
      scheduleTargets
    }),
    [isGlucose, isWeight, summaryPeriodLabel, measurements, summaryPeriodData, activeSummaryRows, scheduleTargets]
  );

  const loadAiSummary = useCallback(async (payload) => {
    if (!practiceId || !patientId) return;
    const requestId = ++aiRequestIdRef.current;
    if (!payload?.readingCount) {
      setAiSummary('');
      setAiSummaryError('');
      setAiSummaryLoading(false);
      return;
    }
    setAiSummaryLoading(true);
    setAiSummaryError('');
    try {
      const res = await measurementService.getMeasurementAiSummary(practiceId, patientId, payload);
      if (requestId !== aiRequestIdRef.current) return;
      const text = res?.data?.data?.summary || res?.data?.summary || '';
      const html = res?.data?.data?.html || '';
      setAiSummary(html || text);
    } catch (err) {
      if (requestId !== aiRequestIdRef.current) return;
      setAiSummary('');
      setAiSummaryError(
        err?.response?.data?.message || err?.message || 'Unable to generate measurement summary.'
      );
    } finally {
      if (requestId === aiRequestIdRef.current) setAiSummaryLoading(false);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!aiBasedAnalysisEnabled) {
      setAiSummary('');
      setAiSummaryError('');
      setAiSummaryLoading(false);
      return undefined;
    }
    if (loadingMeasurements) return undefined;
    const timer = setTimeout(() => {
      loadAiSummary(aiSummaryPayload);
    }, 450);
    return () => clearTimeout(timer);
  }, [aiSummaryPayload, loadingMeasurements, loadAiSummary, aiBasedAnalysisEnabled]);

  // Close export dropdown on outside click
  useEffect(() => {
    if (!showExportMenu) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.bp-export-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleVitalsSelect = (tab) => {
    const config = VITALS_CONFIG.find((v) => v.key === tab);
    if (!config || !vitalEnabled[config.configKey]) return;
    setSelectedVitals(tab);
  };

  const handleReset = () => {
    setDateMode("range");
    setShowCustomRange(false);
    setDateRange("30");
    setFromDate("");
    setToDate("");
    setSortBy("Weekly average");
    toast.info("Filters reset to default");
  };

  const selectRange = (value) => {
    setDateMode("range");
    setShowCustomRange(false);
    setDateRange(value);
  };

  const showTable = viewMode === "table" || viewMode === "combined";
  const showTrend = viewMode === "chart" || viewMode === "charts" || viewMode === "combined";
  const toggleViewPart = (part) => {
    const nextTable = part === "table" ? !showTable : showTable;
    const nextTrend = part === "trend" ? !showTrend : showTrend;
    if (!nextTable && !nextTrend) return;
    setViewMode(nextTable && nextTrend ? "combined" : nextTable ? "table" : "chart");
  };

  const kpiCards = useMemo(() => {
    const list = Array.isArray(measurements) ? measurements : [];
    const counts = { above: 0, within: 0, below: 0 };
    const days = new Set();
    const values = [];
    const sysVals = [];
    const diaVals = [];
    const pulseVals = [];
    const weightTarget = scheduleTargets?.weightTargetRange && Object.keys(scheduleTargets.weightTargetRange).length > 0
      ? scheduleTargets.weightTargetRange
      : { weightMin: 66, weightMax: 220 };

    list.forEach((m) => {
      let status;
      if (isGlucose) {
        const val = readGlucoseValue(m);
        if (val == null) return;
        values.push(val);
        status = checkBGValue(val, getBloodGlucoseTargetForMeasurement(m, scheduleTargets));
      } else if (isWeight) {
        const val = readWeightValue(m);
        if (val == null) return;
        values.push(val);
        status = checkWeightValue(val, weightTarget);
      } else {
        const sys = readRecordedNumber(m.systolic_pressure, m.systolic);
        const dia = readRecordedNumber(m.diastolic_pressure, m.diastolic);
        if (sys == null && dia == null) return;
        if (sys != null) sysVals.push(sys);
        if (dia != null) diaVals.push(dia);
        const pulse = readRecordedNumber(m.pulse);
        if (pulse != null) pulseVals.push(pulse);
        const check = checkBPValues(m, getBloodPressureTargetForMeasurement(m, scheduleTargets));
        if (check.sysStatus === 'high' || check.diaStatus === 'high') status = 'high';
        else if (check.sysStatus === 'low' || check.diaStatus === 'low') status = 'low';
        else status = 'normal';
      }
      if (status === 'high') counts.above += 1;
      else if (status === 'low') counts.below += 1;
      else counts.within += 1;
      const rawDate = m.measurement_date || m.date_recorded || m.created_at || m.date || '';
      if (rawDate) days.add(String(rawDate).split('T')[0]);
    });

    const total = counts.above + counts.within + counts.below;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    const ofTotal = (n) => (total ? `${n} of ${total}` : null);
    const inPct = pct(counts.within);
    const rangeDays = dateMode === 'range' && String(dateRange).toLowerCase() !== 'all'
      ? parseInt(dateRange, 10) || null
      : null;

    const cards = [];
    if (isGlucose || isWeight) {
      const digits = isWeight ? 1 : 0;
      const avg = mean(values);
      cards.push({
        label: isGlucose ? 'Avg glucose' : 'Avg weight',
        value: avg != null ? avg.toFixed(digits) : '—',
        note: avg != null && isGlucose ? 'mg/dL' : null,
      });
      cards.push({
        label: 'Range',
        value: values.length
          ? `${Math.min(...values).toFixed(digits)}–${Math.max(...values).toFixed(digits)}`
          : '—',
      });
    } else {
      const sys = mean(sysVals);
      const dia = mean(diaVals);
      const pulse = mean(pulseVals);
      cards.push({
        label: 'Avg BP',
        value: sys != null || dia != null
          ? `${sys != null ? Math.round(sys) : '—'}/${dia != null ? Math.round(dia) : '—'}`
          : '—',
        note: sys != null || dia != null ? 'mmHg' : null,
      });
      cards.push({
        label: 'Avg pulse',
        value: pulse != null ? String(Math.round(pulse)) : '—',
        note: pulse != null ? 'bpm' : null,
      });
    }
    cards.push({
      label: 'Days with readings',
      value: String(days.size),
      note: rangeDays ? `of ${rangeDays}` : null,
    });
    cards.push({
      label: 'In target',
      value: total ? `${inPct}%` : '—',
      className: total ? (inPct >= 80 ? 'pdx-t-green' : inPct >= 60 ? 'pdx-t-amber' : 'pdx-t-red') : '',
      note: ofTotal(counts.within),
    });
    cards.push({
      label: 'Above target',
      value: total ? `${pct(counts.above)}%` : '—',
      className: counts.above ? 'pdx-t-red' : '',
      note: ofTotal(counts.above),
    });
    cards.push({
      label: 'Below target',
      value: total ? `${pct(counts.below)}%` : '—',
      className: counts.below ? 'pdx-t-amber' : '',
      note: ofTotal(counts.below),
    });
    return cards;
  }, [measurements, isGlucose, isWeight, scheduleTargets, dateMode, dateRange]);

  const patientObj = patientData?.patient || patientData || {};
  const patientName = `${patientObj.last_name || ''}, ${patientObj.first_name || ''}`.trim() || 'Patient';
  const dobRaw = patientObj.date_of_birth || patientObj.dob || patientObj.patient_birthday || '';
  const patientDOB = dobRaw ? dobRaw.split('T')[0] : 'N/A';
  const patientPhone = patientObj.phone_number || patientObj.phone || 'N/A';
  const practiceName = patientObj.practice_name || patientData?.practice_name || '';

  const printedByName = useMemo(() => {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return name || user?.name || user?.username || user?.email || 'System User';
  }, [user]);

  const handlePrint = () => {
    try {
      const el = document.getElementById('rpm-overview-print-area');
      printElement(el || document.getElementById('rpm-overview-container') || document.body, `${selectedVitals} Report - ${patientName}`);
    } catch (e) {
      window.print();
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const resolvedDevice = [deviceSerial, patientData?.device_serial, patientObj?.serial_number]
        .map((value) => String(value || '').trim())
        .find((value) => value && value !== 'N/A' && value !== '—') || 'N/A';

      const configPatient = {
        name: patientName,
        dob: patientDOB,
        phone: patientPhone,
        device: resolvedDevice,
        practice: practiceName,
        printedBy: printedByName
      };

      const defaultBpTarget = {
        systolicMin: 100,
        systolicMax: 140,
        diastolicMin: 60,
        diastolicMax: 90,
        pulseMin: 60,
        pulseMax: 100
      };

      // Group measurements by date (YYYY-MM-DD keys)
      const grouped = {};
      (measurements || []).forEach((m) => {
        const rawD = m.measurement_date || m.date_recorded || m.created_at || m.date || '';
        const d = rawD ? String(rawD).split('T')[0] : 'Unknown';
        if (!grouped[d]) grouped[d] = [];
        grouped[d].push(m);
      });
      const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
      // PDF builders expect [{ label, dates: string[] }], not a flat date-string array
      const dateBuckets = sortedDates.length
        ? [{ label: 'All Dates', dates: sortedDates }]
        : [];

      const bgPeriodNames = BG_PERIOD_COLUMNS.map((c) => c.key);
      const weightPeriodNames = WEIGHT_PERIOD_COLUMNS.map((c) => c.key);
      const bpPeriodNames = BP_PERIOD_COLUMNS.map((c) => c.key);

      const formatDate = (d) => {
        if (!d) return '';
        const parts = String(d).split('-');
        if (parts.length === 3) return `${parts[1]}-${parts[2]}-${parts[0]}`;
        return String(d);
      };
      const formatTime = (t) => {
        if (!t) return '';
        const parts = String(t).split(':');
        if (parts.length >= 2) {
          let h = parseInt(parts[0], 10);
          const mStr = parts[1];
          const ampm = h >= 12 ? 'PM' : 'AM';
          h = h % 12 || 12;
          return `${h}:${mStr} ${ampm}`;
        }
        return String(t);
      };
      const formatPercentCount = (n, t) => (
        t ? `${Math.round((Number(n || 0) / t) * 100)}% (${n || 0})` : `0% (${n || 0})`
      );

      const getBgSummaryPeriodMeta = (periodName) => {
        const key = normalizePeriodName(periodName);
        if (key.includes('wakeup')) return { group: 'Wake-up', sub: null, isSingleHeader: true };
        if (key.includes('beforebreakfast')) return { group: 'Breakfast', sub: 'Before-Meal' };
        if (key.includes('afterbreakfast')) return { group: 'Breakfast', sub: 'After-Meal' };
        if (key.includes('beforelunch')) return { group: 'Lunch', sub: 'Before-Meal' };
        if (key.includes('afterlunch')) return { group: 'Lunch', sub: 'After-Meal' };
        if (key.includes('beforedinner')) return { group: 'Dinner', sub: 'Before-Meal' };
        if (key.includes('afterdinner')) return { group: 'Dinner', sub: 'After-Meal' };
        if (key.includes('bedtime')) return { group: 'Bedtime', sub: null, isSingleHeader: true };
        if (key.includes('midnight')) return { group: 'Midnight', sub: null, isSingleHeader: true };
        return { group: periodName, sub: periodName, isSingleHeader: false };
      };

      if (isGlucose) {
        const calculateGlucosePeriodStats = (allMeasurements, periodName) => {
          const normalizedPeriod = normalizePeriodName(periodName);
          const periodMeasurements = (allMeasurements || []).filter(
            (m) => normalizePeriodName(mapBgPeriod(m)) === normalizedPeriod
          );
          if (!periodMeasurements.length) {
            return { avg: null, max: null, min: null, count: 0, above: 0, below: 0, within: 0 };
          }
          const vals = periodMeasurements.map((m) => readGlucoseValue(m)).filter((v) => v != null);
          let above = 0;
          let below = 0;
          let within = 0;
          periodMeasurements.forEach((m) => {
            const target = getBloodGlucoseTargetForMeasurement(m, scheduleTargets) || { minimum: 60, maximum: 110 };
            const status = checkBGValue(readGlucoseValue(m), target);
            if (status === 'high') above += 1;
            else if (status === 'low') below += 1;
            else within += 1;
          });
          return {
            avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
            max: vals.length ? Math.max(...vals) : null,
            min: vals.length ? Math.min(...vals) : null,
            count: periodMeasurements.length,
            above,
            below,
            within
          };
        };

        const summaryPeriodColumns = bgPeriodNames.map((periodName) => ({
          periodName,
          ...getBgSummaryPeriodMeta(periodName)
        }));
        const beforeMealCols = Math.max(summaryPeriodColumns.filter((col) => col.sub === 'Before-Meal').length, 1);
        const afterMealCols = Math.max(summaryPeriodColumns.filter((col) => col.sub === 'After-Meal').length, 1);
        const remainingCols = Math.max(summaryPeriodColumns.length - beforeMealCols - afterMealCols, 1);

        let above = 0;
        let below = 0;
        let within = 0;
        (measurements || []).forEach((m) => {
          const target = getBloodGlucoseTargetForMeasurement(m, scheduleTargets) || { minimum: 60, maximum: 110 };
          const status = checkBGValue(readGlucoseValue(m), target);
          if (status === 'high') above += 1;
          else if (status === 'low') below += 1;
          else within += 1;
        });

        exportBloodGlucoseTablesPdf({
          filename: `BG-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
          title: `Blood Glucose Report - ${patientName}`,
          patient: configPatient,
          dateBuckets,
          periodNames: bgPeriodNames,
          groupedMeasurements: grouped,
          determinePeriodFromMeasurement: mapBgPeriod,
          formatDate,
          formatTime,
          getGlucoseStatus: (m) => {
            const target = getBloodGlucoseTargetForMeasurement(m, scheduleTargets) || { minimum: 60, maximum: 110 };
            return checkBGValue(readGlucoseValue(m), target);
          },
          effectiveMeasurements: measurements || [],
          calculatePeriodStats: calculateGlucosePeriodStats,
          summaryPeriodColumns,
          rangeTotals: { above, within, below },
          totalReadingCount: above + within + below,
          formatPercentCount,
          totalRowLayout: { beforeMealCols, afterMealCols, remainingCols }
        });
      } else if (isWeight) {
        const weightTarget = (scheduleTargets?.weightTargetRange && Object.keys(scheduleTargets.weightTargetRange).length > 0)
          ? scheduleTargets.weightTargetRange
          : { weightMin: 66, weightMax: 220, bodyFatMin: 14, bodyFatMax: 27, bmiNormal: 18.5, bmiObese: 30 };

        const resolveWeightPeriodMap = (date) => {
          const byPeriod = Object.fromEntries(weightPeriodNames.map((p) => [p, []]));
          (grouped[date] || []).forEach((m) => {
            const resolved = mapBpOrWeightPeriod(m);
            const matched = weightPeriodNames.find(
              (p) => normalizePeriodName(p) === normalizePeriodName(resolved)
            );
            if (matched) byPeriod[matched].push(m);
          });
          return byPeriod;
        };

        const calculateWeightPeriodStats = (allMeasurements, periodName) => {
          const normalizedPeriod = normalizePeriodName(periodName);
          const periodMeasurements = (allMeasurements || []).filter(
            (m) => normalizePeriodName(mapBpOrWeightPeriod(m)) === normalizedPeriod
          );
          const empty = {
            avg: { weight: null, fat: null, bmi: null },
            max: { weight: null, fat: null, bmi: null },
            min: { weight: null, fat: null, bmi: null },
            count: 0,
            above: 0,
            below: 0,
            within: 0
          };
          if (!periodMeasurements.length) return empty;

          const weightValues = periodMeasurements.map((m) => readWeightValue(m)).filter((v) => v != null);
          const fatValues = periodMeasurements.map((m) => Number(m.fat ?? m.body_fat)).filter((v) => !Number.isNaN(v));
          const bmiValues = periodMeasurements.map((m) => Number(m.bmi)).filter((v) => !Number.isNaN(v));
          const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
          const minv = (arr) => (arr.length ? Math.min(...arr) : null);
          const maxv = (arr) => (arr.length ? Math.max(...arr) : null);

          let above = 0;
          let below = 0;
          let within = 0;
          periodMeasurements.forEach((m) => {
            const status = checkWeightValue(readWeightValue(m), weightTarget);
            if (status === 'high') above += 1;
            else if (status === 'low') below += 1;
            else within += 1;
          });

          return {
            avg: { weight: avg(weightValues), fat: avg(fatValues), bmi: avg(bmiValues) },
            max: { weight: maxv(weightValues), fat: maxv(fatValues), bmi: maxv(bmiValues) },
            min: { weight: minv(weightValues), fat: minv(fatValues), bmi: minv(bmiValues) },
            count: periodMeasurements.length,
            above,
            below,
            within
          };
        };

        const calculateOverallTargetStats = () => {
          const stats = {
            above: { weight: 0, bmi: 0, fat: 0 },
            below: { weight: 0, bmi: 0, fat: 0 },
            within: { weight: 0, bmi: 0, fat: 0 }
          };
          (measurements || []).forEach((m) => {
            const weightVal = readWeightValue(m);
            if (weightVal != null) {
              const s = checkWeightValue(weightVal, weightTarget);
              if (s === 'high') stats.above.weight += 1;
              else if (s === 'low') stats.below.weight += 1;
              else stats.within.weight += 1;
            }
            const fatVal = m.fat != null || m.body_fat != null ? Number(m.fat ?? m.body_fat) : null;
            if (fatVal != null && !Number.isNaN(fatVal)) {
              const fatMin = Number(weightTarget.bodyFatMin || 14);
              const fatMax = Number(weightTarget.bodyFatMax || 27);
              if (fatVal > fatMax) stats.above.fat += 1;
              else if (fatVal < fatMin) stats.below.fat += 1;
              else stats.within.fat += 1;
            }
            const bmiVal = m.bmi != null ? Number(m.bmi) : null;
            if (bmiVal != null && !Number.isNaN(bmiVal)) {
              const bmiNormal = Number(weightTarget.bmiNormal || 18.5);
              const bmiObese = Number(weightTarget.bmiObese || 30);
              if (bmiVal >= bmiObese) stats.above.bmi += 1;
              else if (bmiVal < bmiNormal) stats.below.bmi += 1;
              else stats.within.bmi += 1;
            }
          });
          return stats;
        };

        exportWeightTablesPdf({
          filename: `Weight-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
          title: `Weight Report - ${patientName}`,
          patient: configPatient,
          periodList: weightPeriodNames,
          dateBuckets,
          groupedMeasurements: grouped,
          resolvePeriodForMeasurement: resolveWeightPeriodMap,
          formatDate,
          formatTime,
          getWeightStatus: (m) => checkWeightValue(readWeightValue(m), weightTarget),
          effectiveMeasurements: measurements || [],
          calculatePeriodStats: calculateWeightPeriodStats,
          calculateOverallTargetStats,
          formatPercentCount
        });
      } else {
        const calculateBpPeriodStats = (allMeasurements, periodName) => {
          const normalizedPeriod = normalizePeriodName(periodName);
          const filtered = (allMeasurements || []).filter(
            (m) => normalizePeriodName(mapBpOrWeightPeriod(m)) === normalizedPeriod
          );
          if (!filtered.length) {
            return { count: 0, avg: {}, min: {}, max: {} };
          }
          const sysVals = filtered.map((m) => Number(m.systolic_pressure || m.systolic)).filter((v) => !Number.isNaN(v));
          const diaVals = filtered.map((m) => Number(m.diastolic_pressure || m.diastolic)).filter((v) => !Number.isNaN(v));
          const pulseVals = filtered.map((m) => Number(m.pulse)).filter((v) => !Number.isNaN(v) && v > 0);
          const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
          const minv = (arr) => (arr.length ? Math.min(...arr) : null);
          const maxv = (arr) => (arr.length ? Math.max(...arr) : null);
          return {
            count: filtered.length,
            avg: { sys: avg(sysVals), dia: avg(diaVals), pulse: avg(pulseVals) },
            min: { sys: minv(sysVals), dia: minv(diaVals), pulse: minv(pulseVals) },
            max: { sys: maxv(sysVals), dia: maxv(diaVals), pulse: maxv(pulseVals) }
          };
        };

        exportBloodPressureTablesPdf({
          filename: `BP-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
          title: `Blood Pressure Report - ${patientName}`,
          patient: configPatient,
          periodList: bpPeriodNames,
          sortedDates,
          groupedMeasurements: grouped,
          getResolvedPeriodName: mapBpOrWeightPeriod,
          getPeriodInfo: null,
          getReadingStatus: (m) => {
            const bpTarget = getBloodPressureTargetForMeasurement(m, scheduleTargets) || defaultBpTarget;
            const bpCheck = checkBPValues(m, bpTarget);
            return {
              sysStatus: bpCheck.sysStatus,
              diaStatus: bpCheck.diaStatus,
              pulseStatus: checkPulseValue(m.pulse, bpTarget) || 'normal'
            };
          },
          formatDate,
          formatTime,
          effectiveMeasurements: measurements || [],
          calculatePeriodStats: calculateBpPeriodStats,
          totals: null,
          formatPercentCount
        });
      }

      toast.success('Exported to PDF');
      patientService.logPatientExport(practiceId, patientId, {
        format: 'pdf',
        source: isGlucose ? 'Blood Glucose' : isWeight ? 'Weight' : 'Blood Pressure'
      });
    } catch (e) {
      console.error('PDF Export failed:', e);
      toast.error(e?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const formatCsvDate = (m) => {
    if (!m) return 'N/A';
    const raw =
      m.measurement_date ||
      m.date_recorded ||
      m.date ||
      m.measure_date_time ||
      m.measure_new_date_time ||
      m.created_at ||
      '';
    if (!raw) return 'N/A';

    const rawStr = String(raw).trim();
    const dateOnlyStr = rawStr.split('T')[0].split(' ')[0];

    let formatted = '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnlyStr)) {
      const [y, mm, dd] = dateOnlyStr.split('-');
      formatted = `${mm}/${dd}/${y}`;
    } else if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateOnlyStr)) {
      const [y, mm, dd] = dateOnlyStr.split('/');
      formatted = `${mm}/${dd}/${y}`;
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateOnlyStr)) {
      formatted = dateOnlyStr;
    } else {
      try {
        const d = new Date(rawStr);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const y = d.getFullYear();
          formatted = `${mm}/${dd}/${y}`;
        }
      } catch (e) {
        // ignore
      }
    }

    if (!formatted) formatted = dateOnlyStr || 'N/A';

    // Wrap as Excel text formula `="MM/DD/YYYY"` to force text interpretation in Excel
    // preventing `########` display and year truncation.
    return `="${formatted}"`;
  };

  const formatCsvTime = (m) => {
    if (!m) return 'N/A';
    const raw =
      m.measurement_time ||
      m.time_recorded ||
      m.time ||
      (m.measure_date_time && String(m.measure_date_time).includes(' ') ? String(m.measure_date_time).split(' ')[1] : '') ||
      (m.measure_new_date_time && String(m.measure_new_date_time).includes('T') ? String(m.measure_new_date_time).split('T')[1]?.slice(0, 8) : '') ||
      (m.created_at && String(m.created_at).includes('T') ? String(m.created_at).split('T')[1]?.slice(0, 8) : '') ||
      '';
    if (!raw) return 'N/A';
    const clean = String(raw).trim();
    const parts = clean.split(':');
    if (parts.length >= 2) {
      let h = parseInt(parts[0], 10);
      const mStr = parts[1].slice(0, 2);
      if (!isNaN(h)) {
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${mStr} ${ampm}`;
      }
    }
    return clean;
  };

  const resolveMeasurementPeriod = (m) => {
    const p = isGlucose ? mapBgPeriod(m) : mapBpOrWeightPeriod(m);
    return p || 'N/A';
  };

  const buildCsvContentForMeasurements = (measurementsList, filterLabel = 'All') => {
    const rows = [];
    rows.push(['Patient Info']);
    rows.push(['Name', patientName]);
    rows.push(['DOB', patientDOB]);
    rows.push(['Phone', patientPhone]);
    rows.push(['Device', deviceSerial || 'N/A']);
    if (practiceName) rows.push(['Practice', practiceName]);
    rows.push(['Printed By', printedByName]);
    rows.push(['Printed On', new Date().toLocaleString()]);
    rows.push([]);

    rows.push(['Query Period']);
    rows.push(['Date Range', filterLabel]);
    rows.push([]);

    if (isGlucose) {
      rows.push(['Blood Glucose Measurements']);
      rows.push(['Date', 'Period', 'Blood Glucose (mg/dL)', 'Measure Time']);
      (measurementsList || []).forEach((m) => {
        const val = readGlucoseValue(m);
        rows.push([
          formatCsvDate(m),
          resolveMeasurementPeriod(m),
          val != null ? Math.round(val) : '',
          formatCsvTime(m)
        ]);
      });
    } else if (isWeight) {
      rows.push(['Weight Measurements']);
      rows.push(['Date', 'Period', 'Weight (lbs)', 'Body Fat (%)', 'BMI', 'Measure Time']);
      (measurementsList || []).forEach((m) => {
        const wVal = m.weight || m.weight_lbs;
        rows.push([
          formatCsvDate(m),
          resolveMeasurementPeriod(m),
          wVal != null ? Math.round(Number(wVal)) : '',
          m.body_fat != null ? Number(m.body_fat).toFixed(1) : '',
          m.bmi != null ? Number(m.bmi).toFixed(1) : '',
          formatCsvTime(m)
        ]);
      });
    } else {
      rows.push(['Blood Pressure Measurements']);
      rows.push(['Date', 'Period', 'Systolic', 'Diastolic', 'Pulse', 'Measure Time']);
      (measurementsList || []).forEach((m) => {
        const sys = m.systolic_pressure || m.systolic;
        const dia = m.diastolic_pressure || m.diastolic;
        const pulseVal = m.pulse;
        rows.push([
          formatCsvDate(m),
          resolveMeasurementPeriod(m),
          sys != null ? Math.round(Number(sys)) : '',
          dia != null ? Math.round(Number(dia)) : '',
          pulseVal != null && pulseVal !== '' ? Math.round(Number(pulseVal)) : '',
          formatCsvTime(m)
        ]);
      });
    }

    rows.push([]);
    rows.push(['Summary Of Measurement']);
    if (isGlucose) {
      rows.push(['Period', 'Average Glucose', 'Minimum Glucose', 'Maximum Glucose', 'Target Range']);
      (activeSummaryRows || []).forEach((s) => {
        rows.push([s.period, s.primaryAvg ?? s.systolicAvg, s.primaryMin ?? s.systolicMin, s.primaryMax ?? s.systolicMax, s.targetRange]);
      });
    } else if (isWeight) {
      rows.push(['Period', 'Average Weight', 'Minimum Weight', 'Maximum Weight', 'Target Range']);
      (activeSummaryRows || []).forEach((s) => {
        rows.push([s.period, s.primaryAvg ?? s.systolicAvg, s.primaryMin ?? s.systolicMin, s.primaryMax ?? s.systolicMax, s.targetRange]);
      });
    } else {
      rows.push(['Period', 'Systolic Avg', 'Systolic Min', 'Systolic Max', 'Diastolic Avg', 'Diastolic Min', 'Diastolic Max', 'Pulse Avg', 'Pulse Min', 'Pulse Max', 'Target Range']);
      (activeSummaryRows || []).forEach((s) => {
        rows.push([
          s.period,
          s.systolicAvg, s.systolicMin, s.systolicMax,
          s.diastolicAvg, s.diastolicMin, s.diastolicMax,
          s.pulseAvg, s.pulseMin, s.pulseMax,
          s.targetRange
        ]);
      });
    }

    return '\uFEFF' + toCsv(rows);
  };

  const handleExportCsv = () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const currentFilterLabel = dateMode === 'range' ? (dateRange === 'All' ? 'All' : `Last ${dateRange} days`) : `From ${fromDate || 'start'} to ${toDate || 'end'}`;
      const csvString = buildCsvContentForMeasurements(measurements, currentFilterLabel);
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const csvFilename = `${selectedVitals.replace(/\s+/g, '-')}-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadBlob(blob, csvFilename);
      toast.success('Exported to CSV');
      patientService.logPatientExport(practiceId, patientId, {
        format: 'csv',
        source: isGlucose ? 'Blood Glucose' : isWeight ? 'Weight' : 'Blood Pressure'
      });
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const vitalTypeLabel = isGlucose ? 'Blood Glucose' : isWeight ? 'Weight' : 'Blood Pressure';
  const appliedFilterLabel = dateMode === 'range'
    ? (String(dateRange).toLowerCase() === 'all' ? 'All' : `Last ${dateRange} days`)
    : `From ${fromDate || 'start'} to ${toDate || 'end'}`;
  const defaultEmailSubject = `${vitalTypeLabel} Report - ${patientName} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `${vitalTypeLabel} Report\nPatient: ${patientName} (DOB: ${patientDOB})\nQuery Period: ${appliedFilterLabel}\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${measurements?.length || 0}`;

  const handleSendEmail = async (emailData) => {
    try {
      setEmailSending(true);

      const currentFilterLabel = dateMode === 'range'
        ? (String(dateRange).toLowerCase() === 'all' ? 'All' : `Last ${dateRange} days`)
        : `From ${fromDate || 'start'} to ${toDate || 'end'}`;
      const csvContent = buildCsvContentForMeasurements(measurements, currentFilterLabel);
      const csvFilename = `${selectedVitals.replace(/\s+/g, '-')}-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      const csvFile = new File([csvContent], csvFilename, { type: 'text/csv;charset=utf-8;' });

      const userAttachments = Array.isArray(emailData.attachments) ? emailData.attachments : [];
      const attachments = [csvFile, ...userAttachments];

      const eventId = isGlucose ? 10 : isWeight ? 11 : 9;
      const res = await sendReportEmail({ ...emailData, attachments }, { event_id: eventId });
      if (res && res.success === false) throw new Error(res.error || res.message || 'Failed to send email');
      toast.success('Email queued successfully');
      patientService.logPatientExport(practiceId, patientId, {
        format: 'email',
        source: vitalTypeLabel
      });
      setShowEmailModal(false);
    } catch (e) {
      console.error('Failed to send email:', e);
      toast.error(e?.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div id="rpm-overview-container" className="patient-details-rpm-overview pd-overview">

      {/* Toolbar: vitals, date range, grouping, view toggles, actions */}
      <div className="pdx-row pdx-toolbar-row">
        <div className="pdx-toolbar-left">
        <div className="pdx-chip-group" role="tablist" aria-label="Vitals">
          {VITALS_CONFIG.map(({ key, label, configKey }) => {
            const enabled = vitalEnabled[configKey];
            const isActive = enabled && selectedVitals === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                className={`pdx-chip${isActive ? ' active' : ''}`}
                disabled={!enabled}
                title={!enabled ? "Not assigned for this patient" : undefined}
                aria-selected={isActive}
                onClick={() => handleVitalsSelect(key)}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="pdx-divider" />

        <div className="pdx-chip-group" aria-label="Date range">
          {RANGE_CHIPS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`pdx-chip${dateMode === "range" && String(dateRange) === value ? ' active' : ''}`}
              onClick={() => selectRange(value)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className={`pdx-chip${dateMode === "fromto" ? ' active' : ''}`}
            onClick={() => {
              if (dateMode === "fromto") {
                setShowCustomRange((visible) => !visible);
              } else {
                setDateMode("fromto");
                setShowCustomRange(true);
              }
            }}
            aria-expanded={dateMode === "fromto" && showCustomRange}
          >
            Custom
          </button>
        </div>

        {dateMode === "fromto" && showCustomRange ? (
          <div className="pdx-custom-range">
            <AppDateInput
              value={fromDate}
              max={toDate && toDate < todayIso ? toDate : todayIso}
              onChange={setFromDate}
              aria-label="From date"
            />
            <span className="to">to</span>
            <AppDateInput
              value={toDate}
              min={fromDate || undefined}
              max={todayIso}
              onChange={setToDate}
              aria-label="To date"
            />
          </div>
        ) : null}

        <select
          className="pdx-chip pdx-select-chip"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label="Sort data by"
          title="Sort data by"
        >
          <option>Weekly average</option>
          <option>Daily average</option>
          <option>Monthly average</option>
          <option>Date</option>
        </select>
        </div>

        <div className="pdx-toolbar-right bp-export-container">
          <div className="pdx-chip pdx-view-chip">
            <button
              type="button"
              className={showTable ? 'on' : ''}
              aria-pressed={showTable}
              title="Data table"
              aria-label="Data table"
              onClick={() => toggleViewPart("table")}
            >
              <TableIcon />
            </button>
            <button
              type="button"
              className={showTrend ? 'on' : ''}
              aria-pressed={showTrend}
              title="Trend chart"
              aria-label="Trend chart"
              onClick={() => toggleViewPart("trend")}
            >
              <ChartIcon />
            </button>
          </div>

          {!isReadOnly ? (
            <button
              type="button"
              className="pdx-chip solid"
              disabled={!isSelectedVitalAddable}
              title={
                !isSelectedVitalEnabled
                  ? "Not assigned for this patient"
                  : !isSelectedVitalAddable
                    ? PRACTICE_VITAL_ADD_DISABLED_TITLE
                    : undefined
              }
              onClick={() => {
                if (!isSelectedVitalAddable) return;
                setShowAddModal(true);
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add data
            </button>
          ) : null}

          <button
            type="button"
            className="pdx-chip icon"
            aria-label="More actions"
            title={isExporting ? "Exporting…" : "More actions"}
            aria-expanded={showExportMenu}
            onClick={() => setShowExportMenu((prev) => !prev)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
            </svg>
          </button>

          {showExportMenu ? (
            <div className="pdx-popover pdx-menu-panel" role="menu">
              <button
                type="button"
                role="menuitem"
                className="pdx-menu-item"
                onClick={() => {
                  setShowExportMenu(false);
                  handlePrint();
                }}
              >
                Print
              </button>
              <button type="button" role="menuitem" className="pdx-menu-item" onClick={handleExportPdf} disabled={isExporting}>
                Export PDF
              </button>
              <button type="button" role="menuitem" className="pdx-menu-item" onClick={handleExportCsv} disabled={isExporting}>
                Export CSV
              </button>
              <button
                type="button"
                role="menuitem"
                className="pdx-menu-item"
                onClick={() => {
                  setShowExportMenu(false);
                  setShowEmailModal(true);
                }}
              >
                Email report
              </button>
              <div className="pdx-menu-sep" />
              <button
                type="button"
                role="menuitem"
                className="pdx-menu-item"
                onClick={() => {
                  setShowExportMenu(false);
                  if (!aiBasedAnalysisEnabled) {
                    navigate?.('/settings?tab=account');
                    return;
                  }
                  setAiSummaryExpanded(true);
                  document.getElementById('rpm-ai-summary')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                AI based analysis
                <span className="pdx-menu-hint">{aiBasedAnalysisEnabled ? 'On' : 'Settings'}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="pdx-menu-item"
                onClick={() => {
                  setShowExportMenu(false);
                  handleReset();
                }}
              >
                Reset filters
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* PRINT-AREA: wraps measurement table + chart + summary for Print/PDF */}
      <div id="rpm-overview-print-area">

        {/* Print-only patient header — hidden in normal view */}
        <div style={{ display: "none" }} className="rpm-print-only-header">
          <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "4px" }}>
            {isGlucose ? "Blood Glucose" : isWeight ? "Weight" : "Blood Pressure"} Report - {patientName}
          </div>
          <div style={{ fontSize: "12px", color: "#64748b" }}>
            Patient: {patientName} &nbsp;|&nbsp;
            DOB: {patientDOB} &nbsp;|&nbsp;
            Phone: {patientPhone} &nbsp;|&nbsp;
            {practiceName && <span>Practice: {practiceName} &nbsp;|&nbsp;</span>}
            Printed By: {printedByName} &nbsp;|&nbsp;
            Printed On: {new Date().toLocaleString()} &nbsp;|&nbsp;
            Period: {dateMode === 'range' ? (dateRange === 'All' ? 'All' : `Last ${dateRange} days`) : `${fromDate || 'start'} to ${toDate || 'end'}`}
          </div>
        </div>

        <div className="pdx-kpi-grid">
          {kpiCards.map((card) => (
            <div className="pdx-kpi" key={card.label}>
              <div className="pdx-kpi-label">{card.label}</div>
              <div className="pdx-kpi-value">
                <span className={card.className || undefined}>{card.value}</span>
                {card.note ? <small>{card.note}</small> : null}
              </div>
            </div>
          ))}
        </div>

      {/* AI Based analysis — heading outside the card; card is expandable */}
      {aiBasedAnalysisEnabled && (
      <div id="rpm-ai-summary" className="pd-ai-summary-wrap" style={{ margin: "12px 20px 0" }}>
        <div className="pd-ai-summary-kicker-row">
          <div className="pd-ai-summary-kicker">AI Based Analysis</div>
          <p className="pd-ai-summary-disabled-hint" data-export-ignore>
            You can disable this from{' '}
            <button
              type="button"
              className="pd-ai-summary-disabled-link"
              onClick={() => navigate?.('/settings?tab=account')}
            >
              Settings
            </button>
            .
          </p>
        </div>
        <div className={`pd-ai-summary${aiSummaryExpanded ? '' : ' is-collapsed'}`}>
          <div
            className="pd-ai-summary-header"
            onClick={() => setAiSummaryExpanded((prev) => !prev)}
          >
            <div className="pd-ai-summary-title">
              {isGlucose ? 'Blood Glucose' : isWeight ? 'Weight' : 'Blood Pressure'} · {summaryPeriodLabel}
            </div>
            <button
              type="button"
              className="pd-ai-summary-chevron"
              onClick={(e) => {
                e.stopPropagation();
                setAiSummaryExpanded((prev) => !prev);
              }}
              aria-expanded={aiSummaryExpanded}
              aria-label={aiSummaryExpanded ? 'Collapse AI based analysis' : 'Expand AI based analysis'}
              data-export-ignore
            >
              <FontAwesomeIcon icon={aiSummaryExpanded ? faChevronUp : faChevronDown} />
            </button>
          </div>
          <div className="pd-ai-summary-content">
            {aiSummaryLoading && !aiSummary && (
              <p className="pd-ai-summary-body pd-ai-summary-muted">Building analysis from patient measurements…</p>
            )}
            {!aiSummaryLoading && !aiSummaryPayload.readingCount && (
              <p className="pd-ai-summary-body pd-ai-summary-muted">No readings in this date range to summarize.</p>
            )}
            {aiSummaryError && (
              <p className="pd-ai-summary-body pd-ai-summary-error">{aiSummaryError}</p>
            )}
            {aiSummary && (
              <div
                className="pd-ai-summary-body"
                dangerouslySetInnerHTML={{ __html: aiSummary }}
              />
            )}
          </div>
        </div>
      </div>
      )}

      {/* 3. MEASUREMENTS TABLE SECTION (table or combined mode — FIRST in Combined View) */}
      {showTable && (
        <div className={`pdx-block${!isGlucose && !isWeight ? ' pdx-readings-panel' : ''}`}>
          <div className="pdx-block-head">
            <div className="pdx-block-title">
              {isGlucose ? "Blood glucose readings" : isWeight ? "Weight readings" : "Blood pressure readings"}
            </div>
            <div className="pdx-block-sub">
              {dateMode === 'range'
                ? (dateRange === 'All' ? 'All dates' : `Last ${dateRange} days`)
                : `${fromDate || '—'} to ${toDate || '—'}`
              }
            </div>
          </div>

          {!isGlucose && !isWeight ? (
            <div className="bphr-grid-container">
              <table className="bphr-table">
                <colgroup>
                  <col style={{ width: '88px' }} />
                  {periodColumns.map((col) => (
                    <React.Fragment key={col.key}>
                      <col style={{ width: '90px' }} />
                      <col style={{ width: '90px' }} />
                      <col style={{ width: '90px' }} />
                    </React.Fragment>
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2} className="datehd">Date</th>
                    {periodColumns.map((col) => (
                      <th key={col.key} colSpan={3} className="grp">
                        {col.label}
                        {col.range ? <span className="hds">{col.range}</span> : null}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {periodColumns.map((col) => (
                      <React.Fragment key={`${col.key}-sub`}>
                        <th className="sub">BP</th>
                        <th className="sub">Pulse</th>
                        <th className="sub">Status</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeTableRows && activeTableRows.length > 0 ? (
                    activeTableRows.flatMap((rowItem, dateIdx) => {
                      const slotRows = expandPeriodSlotRows(rowItem, periodColumns);
                      const dayClass = dateIdx % 2 === 0 ? 'bphr-day-even' : 'bphr-day-odd';
                      return slotRows.map((slotRow, slotIdx) => (
                      <tr key={`${rowItem.dateKey}-${slotIdx}`} className={`reading-row ${dayClass}`}>
                        {slotIdx === 0 && (
                          <td className="cell date-col" rowSpan={slotRows.length}>
                            {rowItem.dateDisplay}
                          </td>
                        )}
                        {periodColumns.map((col) => {
                          const r = slotRow.slots[col.key];

                          if (!r) {
                            return (
                              <React.Fragment key={col.key}>
                                <td className="cell metric bphr-bp-cell">{slotRow.showDate ? <span className="empty">—</span> : null}</td>
                                <td className="cell metric bphr-pulse-cell">{slotRow.showDate ? <span className="empty">—</span> : null}</td>
                                <td className="cell bphr-status-cell" />
                              </React.Fragment>
                            );
                          }

                          const sys = r.systolic_pressure || r.systolic;
                          const dia = r.diastolic_pressure || r.diastolic;
                          const sysVal = sys != null ? Math.round(Number(sys)) : '—';
                          const diaVal = dia != null ? Math.round(Number(dia)) : '—';
                          const bpTarget = getBloodPressureTargetForMeasurement(r, scheduleTargets);
                          const bpCheck = checkBPValues(r, bpTarget);
                          const sysColor = getAlertStatusColor(bpCheck.sysStatus);
                          const diaColor = getAlertStatusColor(bpCheck.diaStatus);
                          const timeStr = formatMeasurementTimeLabel(r);
                          const pulseVal = r.pulse != null && r.pulse !== '' ? Math.round(Number(r.pulse)) : null;
                          const pulseStatus = checkPulseValue(pulseVal, bpTarget);
                          const pulseColor = getAlertStatusColor(pulseStatus);
                          const isAbnormalPulse = pulseStatus === 'high' || pulseStatus === 'low';
                          const isAbnormalBp =
                            bpCheck.sysStatus === 'high' ||
                            bpCheck.sysStatus === 'low' ||
                            bpCheck.diaStatus === 'high' ||
                            bpCheck.diaStatus === 'low' ||
                            isAbnormalPulse;

                          return (
                            <React.Fragment key={col.key}>
                              <td className="cell metric bphr-bp-cell">
                                <div
                                  className="bphr-metric bphr-metric--interactive"
                                  tabIndex={0}
                                  title={`BP: ${sysVal}/${diaVal}${timeStr ? ` at ${timeStr}` : ''}`}
                                >
                                  <div className="bphr-metric-value">
                                    <span style={{ color: sysColor }}>{sysVal}</span>
                                    <span style={{ color: "#64748b", margin: "0 1px" }}>/</span>
                                    <span style={{ color: diaColor }}>{diaVal}</span>
                                  </div>
                                  {timeStr ? <div className="bphr-metric-label">{timeStr}</div> : null}
                                </div>
                              </td>
                              <td className="cell metric bphr-pulse-cell">
                                <div
                                  className="bphr-metric bphr-metric--interactive"
                                  tabIndex={0}
                                  title={pulseVal != null ? `Pulse: ${pulseVal} bpm` : 'No pulse recorded'}
                                >
                                  <div className="bphr-metric-value">
                                    {pulseVal != null ? (
                                      <span className="bphr-pulse-val-wrap">
                                        <span style={{ color: pulseColor }}>{pulseVal}</span>
                                        <PulseIcon isAbnormal={isAbnormalPulse} size={12} />
                                      </span>
                                    ) : (
                                      <span className="empty">—</span>
                                    )}
                                  </div>
                                  {timeStr ? <div className="bphr-metric-label bphr-metric-spacer" aria-hidden="true">&nbsp;</div> : null}
                                </div>
                              </td>
                              <td className="cell bphr-status-cell">
                                {isAbnormalBp ? (
                                  <div className="bphr-metric">
                                    <div className="bphr-metric-value">
                                      <ReadingReviewBadge
                                        reading={r}
                                        isAbnormal
                                        practiceId={practiceId}
                                        patientId={patientId}
                                        vitalType="bp"
                                        onReviewed={handleReadingReviewed}
                                        disabled={isReadOnly}
                                        caregiverOptions={internalCaregivers}
                                        providerOptions={internalProviders}
                                      />
                                    </div>
                                    {timeStr ? <div className="bphr-metric-label bphr-metric-spacer" aria-hidden="true">&nbsp;</div> : null}
                                  </div>
                                ) : null}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ));
                  })
                  ) : (
                    <tr>
                      <td colSpan={(periodColumns.length * 3) + 1} style={{ padding: "24px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                        {loadingMeasurements
                          ? 'Loading measurements…'
                          : isReadOnly
                            ? <>No {selectedVitals.toLowerCase()} measurements found for this period.</>
                          : isSelectedVitalAddable
                            ? <>No {selectedVitals.toLowerCase()} measurements found for this period. Click <strong>Add Data</strong> to add a reading.</>
                            : <>No {selectedVitals.toLowerCase()} measurements found for this period.{!isSelectedVitalEnabled ? '' : ' New readings cannot be added because this vital is no longer enabled for the practice.'}</>}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`bphr-grid-container${isGlucose ? ' bphr-grid-container--glucose' : ''}`}>
              <table className={`bphr-table${isGlucose ? ' bphr-table--glucose' : ''}`}>
                <colgroup>
                  <col style={{ width: isGlucose ? '102px' : '88px' }} />
                  {periodColumns.map((col) => (
                    <React.Fragment key={col.key}>
                      <col style={isGlucose ? undefined : { width: '110px' }} />
                      <col style={isGlucose ? undefined : { width: '124px' }} />
                    </React.Fragment>
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2} className="datehd">Date</th>
                    {periodColumns.map((col) => (
                      <th key={col.key} colSpan={2} className="grp">
                        {col.label}
                        {col.range ? <span className="hds">{col.range}</span> : null}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {periodColumns.map((col) => (
                      <React.Fragment key={`${col.key}-sub`}>
                        <th className="sub">{isGlucose ? 'Glucose' : 'Weight'}</th>
                        <th className="sub">Status</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeTableRows && activeTableRows.length > 0 ? (
                    activeTableRows.flatMap((rowItem, dateIdx) => {
                      const slotRows = expandPeriodSlotRows(rowItem, periodColumns);
                      const dayClass = dateIdx % 2 === 0 ? 'bphr-day-even' : 'bphr-day-odd';
                      return slotRows.map((slotRow, slotIdx) => (
                      <tr key={`${rowItem.dateKey}-${slotIdx}`} className={`reading-row ${dayClass}`}>
                        {slotIdx === 0 && (
                          <td className="cell date-col" rowSpan={slotRows.length}>{rowItem.dateDisplay}</td>
                        )}
                        {periodColumns.map((col) => {
                          const r = slotRow.slots[col.key];

                          if (!r) {
                            return (
                              <React.Fragment key={col.key}>
                                <td className="cell metric">{slotRow.showDate ? <span className="empty">—</span> : null}</td>
                                <td className="cell bphr-status-cell" />
                              </React.Fragment>
                            );
                          }

                          const getReadingAlertStatus = (m) => {
                            if (!m) return 'normal';
                            if (isGlucose) {
                              const val = readGlucoseValue(m);
                              const target = getBloodGlucoseTargetForMeasurement(m, scheduleTargets);
                              return checkBGValue(val, target);
                            }
                            const val = readWeightValue(m);
                            const target = (scheduleTargets?.weightTargetRange && Object.keys(scheduleTargets.weightTargetRange).length > 0)
                              ? scheduleTargets.weightTargetRange
                              : { weightMin: 66, weightMax: 220 };
                            return checkWeightValue(val, target);
                          };

                          const renderReadingValue = (m) => {
                            if (isGlucose) {
                              const val = readGlucoseValue(m);
                              const target = getBloodGlucoseTargetForMeasurement(m, scheduleTargets);
                              const alertStatus = checkBGValue(val, target);
                              return (
                                <span style={{ color: getAlertStatusColor(alertStatus) }}>
                                  {val != null ? Math.round(val) : '—'}
                                </span>
                              );
                            }
                            const val = readWeightValue(m);
                            const target = (scheduleTargets?.weightTargetRange && Object.keys(scheduleTargets.weightTargetRange).length > 0)
                              ? scheduleTargets.weightTargetRange
                              : { weightMin: 66, weightMax: 220 };
                            const alertStatus = checkWeightValue(val, target);
                            return (
                              <span style={{ color: getAlertStatusColor(alertStatus) }}>
                                {val != null ? Number(val).toFixed(1) : '—'}
                              </span>
                            );
                          };

                          const timeStr = formatMeasurementTimeLabel(r);

                          return (
                            <React.Fragment key={col.key}>
                              <td className="cell metric">
                                <div
                                  className="bphr-metric bphr-metric--interactive"
                                  tabIndex={0}
                                  title={`${isGlucose ? 'Glucose' : 'Weight'}${timeStr ? ` at ${timeStr}` : ''}`}
                                >
                                  <div className="bphr-metric-value">
                                    {renderReadingValue(r)}
                                  </div>
                                  {isWeight && (r.fat != null || r.body_fat != null) ? (() => {
                                    const fTarget = scheduleTargets?.weightTargetRange || { bodyFatMin: 14, bodyFatMax: 27 };
                                    const fStatus = checkFatValue(r.fat ?? r.body_fat, fTarget);
                                    const fColor = getVitalStatusColor(fStatus);
                                    return (
                                      <div className="bphr-metric-label" style={{ color: fColor, fontWeight: 600 }}>
                                        Fat {Number(r.fat ?? r.body_fat).toFixed(1)}
                                      </div>
                                    );
                                  })() : null}
                                  {isWeight && r.bmi != null ? (() => {
                                    const bTarget = scheduleTargets?.weightTargetRange || {};
                                    const bStatus = getBmiCategoryStatus(r.bmi, {
                                      bmiNormal: bTarget.bmiNormal || 18.5,
                                      bmiOverweight: bTarget.bmiOverweight || 25,
                                      bmiObese: bTarget.bmiObese || 30
                                    });
                                    const bColor = getVitalStatusColor(bStatus);
                                    return (
                                      <div className="bphr-metric-label" style={{ color: bColor, fontWeight: 600 }}>
                                        BMI {Number(r.bmi).toFixed(1)}
                                      </div>
                                    );
                                  })() : null}
                                  {timeStr ? <div className="bphr-metric-label">{timeStr}</div> : null}
                                </div>
                              </td>
                              <td className="cell bphr-status-cell">
                                {isAbnormalVitalStatus(getReadingAlertStatus(r)) ? (
                                  <div className="bphr-metric">
                                    <div className="bphr-metric-value">
                                      <ReadingReviewBadge
                                        reading={r}
                                        isAbnormal
                                        practiceId={practiceId}
                                        patientId={patientId}
                                        vitalType={isGlucose ? 'bg' : isWeight ? 'wt' : 'bp'}
                                        onReviewed={handleReadingReviewed}
                                        disabled={isReadOnly}
                                        caregiverOptions={internalCaregivers}
                                        providerOptions={internalProviders}
                                      />
                                    </div>
                                    {timeStr ? <div className="bphr-metric-label bphr-metric-spacer" aria-hidden="true">&nbsp;</div> : null}
                                  </div>
                                ) : null}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ));
                  })
                  ) : (
                    <tr>
                      <td colSpan={(periodColumns.length * 2) + 1} style={{ padding: "24px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                        {loadingMeasurements
                          ? 'Loading measurements…'
                          : isReadOnly
                            ? <>No {selectedVitals.toLowerCase()} measurements found for this period.</>
                          : isSelectedVitalAddable
                            ? <>No {selectedVitals.toLowerCase()} measurements found for this period. Click <strong>Add Data</strong> to add a reading.</>
                            : <>No {selectedVitals.toLowerCase()} measurements found for this period.{!isSelectedVitalEnabled ? '' : ' New readings cannot be added because this vital is no longer enabled for the practice.'}</>}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 4. CHARTS SECTION */}
      {showTrend && (
        <div className="pdx-analysis">
          {isWeight ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {/* Combined standard day chart */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px" }}>
                <div className="pdx-panel-head">
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f2942" }}>
                    Standard Day Chart — Weight, Fat and BMI
                  </div>
                  <div className="pdx-legend">
                    <span><i style={{ background: "#2563eb" }} />Weight (lb)</span>
                    <span><i style={{ background: "#f59e0b" }} />Body Fat (%)</span>
                    <span><i style={{ background: "#16a34a" }} />BMI</span>
                  </div>
                </div>
                {combinedWeightTrendData.length === 0 ? (
                  <div style={{ padding: "48px 16px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                    {loadingMeasurements ? 'Loading chart…' : 'No weight readings to chart for this period.'}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={combinedWeightTrendData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#718096" }} />
                      <YAxis
                        yAxisId="weight"
                        tick={{ fontSize: 10, fill: "#2563eb" }}
                        domain={['dataMin - 5', 'dataMax + 5']}
                        label={{ value: "Weight (lb)", angle: -90, position: "insideLeft", style: { fontSize: "10px", fill: "#2563eb" } }}
                      />
                      <YAxis
                        yAxisId="composition"
                        orientation="right"
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        domain={['dataMin - 2', 'dataMax + 2']}
                        label={{ value: "Fat (%) / BMI", angle: 90, position: "insideRight", style: { fontSize: "10px", fill: "#64748b" } }}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          Number(value).toFixed(1),
                          name === 'weight' ? 'Weight (lb)' : name === 'fat' ? 'Body Fat (%)' : 'BMI'
                        ]}
                      />
                      <Line yAxisId="weight" type="monotone" dataKey="weight" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line yAxisId="composition" type="monotone" dataKey="fat" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line yAxisId="composition" type="monotone" dataKey="bmi" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="pdx-analysis-pair">
                {/* Mean Chart of Weight */}
                <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px", minWidth: 0 }}>
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f2942", marginBottom: "12px" }}>
                    Mean Chart of Weight
                  </div>
                  <MeanChartWeight measurements={measurements} scheduleTargets={scheduleTargets} />
                </div>

                {/* Mean Chart of Fat */}
                <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px", minWidth: 0 }}>
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f2942", marginBottom: "12px" }}>
                    Mean Chart of Fat
                  </div>
                  <MeanChartFat measurements={measurements} scheduleTargets={scheduleTargets} />
                </div>
              </div>

              {/* Pie Chart of Weight */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px" }}>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f2942", marginBottom: "12px" }}>
                  Pie Chart of Weight
                </div>
                <PieChartWeight measurements={measurements} scheduleTargets={scheduleTargets} />
              </div>

              {/* Pie Chart of Fat */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px" }}>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f2942", marginBottom: "12px" }}>
                  Pie Chart of Fat
                </div>
                <PieChartFat measurements={measurements} scheduleTargets={scheduleTargets} />
              </div>
            </div>
          ) : (
            <div className="pdx-analysis-pair">
              <div className="pdx-panel">
                <div className="pdx-panel-head">
                  <div className="pdx-panel-title">
                    Trend — {isGlucose ? "Blood glucose" : "Blood pressure"}
                  </div>
                  {isGlucose ? (
                    <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "#4a5568" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: "12px", height: "3px", background: "#00b6c6", borderRadius: "2px" }}></div>
                        Blood Glucose (mg/dL)
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "#4a5568" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: "12px", height: "3px", background: "#e53e3e", borderRadius: "2px" }}></div>
                        Systolic (mmHg)
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: "12px", height: "3px", background: "#1a3550", borderRadius: "2px" }}></div>
                        Diastolic (mmHg)
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  {activeChartData.length === 0 ? (
                    <div style={{ padding: "48px 16px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                      {loadingMeasurements
                        ? 'Loading chart…'
                        : `No ${isGlucose ? 'blood glucose' : 'blood pressure'} readings to chart for this period.`}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      {isGlucose ? (
                        <LineChart data={activeChartData} margin={{ top: 10, right: 16, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#718096" }} />
                          <YAxis
                            domain={[60, 300]}
                            tick={{ fontSize: 10, fill: "#718096" }}
                            label={{ value: "mg/dL", angle: -90, position: "insideLeft", style: { fontSize: "10px", fill: "#718096" } }}
                          />
                          <Tooltip content={<CustomTooltip chartType="glucose" />} />
                          <ReferenceArea y1={70} y2={140} fill="rgba(72,187,120,0.12)" stroke="none" />
                          <ReferenceLine y={70} stroke="#48bb78" strokeDasharray="4 2" label={{ value: "Low", fontSize: 9, fill: "#48bb78" }} />
                          <ReferenceLine y={140} stroke="#e67e22" strokeDasharray="4 2" label={{ value: "High", fontSize: 9, fill: "#e67e22" }} />
                          <Line
                            type="monotone"
                            dataKey="glucose"
                            name="Glucose"
                            stroke="#00b6c6"
                            strokeWidth={2}
                            dot={{ fill: "#00b6c6", r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      ) : (
                        <LineChart data={activeChartData} margin={{ top: 10, right: 16, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#718096" }} />
                          <YAxis
                            domain={['dataMin - 10', 'dataMax + 10']}
                            tick={{ fontSize: 10, fill: "#718096" }}
                            label={{ value: "mmHg", angle: -90, position: "insideLeft", style: { fontSize: "10px", fill: "#718096" } }}
                          />
                          <Tooltip content={<CustomTooltip chartType="bp" />} />
                          <ReferenceArea y1={90} y2={140} fill="rgba(144,238,144,0.15)" stroke="none" />
                          <ReferenceLine y={90} stroke="#48bb78" strokeDasharray="4 2" />
                          <ReferenceLine y={140} stroke="#e67e22" strokeDasharray="4 2" label={{ value: "Target Zone (140)", position: "insideTopLeft", fontSize: 10, fill: "#2e7d32", dy: -10 }} />
                          <Line
                            type="monotone"
                            dataKey="systolic"
                            name="Systolic"
                            stroke="#e53e3e"
                            strokeWidth={2}
                            dot={{ fill: "#e53e3e", r: 4 }}
                            activeDot={{ r: 6 }}
                            connectNulls
                          />
                          <Line
                            type="monotone"
                            dataKey="diastolic"
                            name="Diastolic"
                            stroke="#1a3550"
                            strokeWidth={2}
                            dot={{ fill: "#1a3550", r: 4 }}
                            activeDot={{ r: 6 }}
                            connectNulls
                          />
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="pdx-panel">
                <div className="pdx-panel-head">
                  <div className="pdx-panel-title">
                    Mean chart — {isGlucose ? "Blood glucose" : "Blood pressure"}
                  </div>
                  {isGlucose ? (
                    <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "#4a5568" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: "12px", height: "3px", background: "#00b6c6", borderRadius: "2px" }}></div>
                        Blood Glucose (mg/dL)
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "#4a5568" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: "12px", height: "3px", background: "#e53e3e", borderRadius: "2px" }}></div>
                        Systolic (mmHg)
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: "12px", height: "3px", background: "#1a3550", borderRadius: "2px" }}></div>
                        Diastolic (mmHg)
                      </div>
                    </div>
                  )}
                </div>
                {isGlucose ? (
                  <MeanChartBG practiceId={practiceId} patientId={patientId} measurements={measurements} scheduleTargets={scheduleTargets} embedded={true} />
                ) : (
                  <MeanChart practiceId={practiceId} patientId={patientId} measurements={measurements} scheduleTargets={scheduleTargets} embedded={true} />
                )}
              </div>
            </div>
          )}

          {false && (
            <div className={`pdx-analysis-pair${isWeight ? ' single' : ''}`}>
        {/* Summary of measurement */}
        <div className="pdx-panel">
          <div className="pdx-panel-head">
            <div>
              <div className="pdx-panel-title">Summary of measurement</div>
              <div className="pdx-block-sub">Average (min–max) per period</div>
            </div>
            <div style={{ display: "flex", gap: "12px", fontSize: "11px", color: "#64748b" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "3px", background: "#dc2626", display: "inline-block", borderRadius: "2px" }}></span> Above
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "3px", background: "#16a34a", display: "inline-block", borderRadius: "2px" }}></span> Within
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "3px", background: "#ea580c", display: "inline-block", borderRadius: "2px" }}></span> Below
              </span>
            </div>
          </div>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "6px", overflowX: "auto", background: "#ffffff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "#0077b6", color: "#ffffff", textAlign: "left" }}>
                  <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff" }}>PERIOD</th>
                  {summaryPeriodData.kind === "glucose" ? (
                    <>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>GLUCOSE</th>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>COUNT</th>
                    </>
                  ) : summaryPeriodData.kind === "weight" ? (
                    <>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>WEIGHT</th>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>BODY FAT</th>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>BMI</th>
                    </>
                  ) : (
                    <>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>SYSTOLIC</th>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>DIASTOLIC</th>
                      <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff", textAlign: "center" }}>PULSE</th>
                    </>
                  )}
                  <th style={{ padding: "11px 14px", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em", color: "#ffffff" }}>TARGET RANGE</th>
                </tr>
              </thead>
              <tbody>
                {summaryPeriodData.periodList.map((pName, pIdx) => {
                  const stats = summaryPeriodData.periodStats[pName] || {};
                  const calcAvg = (arr) => arr?.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : '—';
                  const calcMin = (arr) => arr?.length ? Math.min(...arr) : '—';
                  const calcMax = (arr) => arr?.length ? Math.max(...arr) : '—';
                  const calcFmtAvg = (arr) => arr?.length ? (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1) : '—';
                  const calcFmtMin = (arr) => arr?.length ? Math.min(...arr).toFixed(1) : '—';
                  const calcFmtMax = (arr) => arr?.length ? Math.max(...arr).toFixed(1) : '—';

                  const totalSeg = (stats.above || 0) + (stats.within || 0) + (stats.below || 0);

                  return (
                    <tr key={pName} style={{ borderBottom: pIdx < summaryPeriodData.periodList.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: "12px", fontWeight: 700, color: "#1a3550" }}>{pName}</span>
                          {summaryPeriodData.periodTimeRanges[pName] && (
                            <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 500 }}>{summaryPeriodData.periodTimeRanges[pName]}</span>
                          )}
                        </div>
                      </td>
                      {summaryPeriodData.kind === "glucose" ? (
                        stats.vals?.length > 0 ? (
                          <>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcAvg(stats.vals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcMin(stats.vals)}–{calcMax(stats.vals)})</span>
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "12.5px", fontWeight: 700, color: "#1a3550" }}>
                              {stats.count}
                            </td>
                          </>
                        ) : (
                          <td colSpan={2} style={{ padding: "10px 12px", textAlign: "center", fontStyle: "italic", color: "#94a3b8", fontSize: "12px" }}>
                            No readings
                          </td>
                        )
                      ) : summaryPeriodData.kind === "weight" ? (
                        stats.weightVals?.length > 0 ? (
                          <>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcFmtAvg(stats.weightVals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcFmtMin(stats.weightVals)}–{calcFmtMax(stats.weightVals)})</span>
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcFmtAvg(stats.fatVals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcFmtMin(stats.fatVals)}–{calcFmtMax(stats.fatVals)})</span>
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcFmtAvg(stats.bmiVals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcFmtMin(stats.bmiVals)}–{calcFmtMax(stats.bmiVals)})</span>
                            </td>
                          </>
                        ) : (
                          <td colSpan={3} style={{ padding: "10px 12px", textAlign: "center", fontStyle: "italic", color: "#94a3b8", fontSize: "12px" }}>
                            No readings
                          </td>
                        )
                      ) : (
                        stats.sysVals?.length > 0 || stats.diaVals?.length > 0 ? (
                          <>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcAvg(stats.sysVals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcMin(stats.sysVals)}–{calcMax(stats.sysVals)})</span>
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcAvg(stats.diaVals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcMin(stats.diaVals)}–{calcMax(stats.diaVals)})</span>
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a3550" }}>{calcAvg(stats.pulseVals)}</span>{' '}
                              <span style={{ fontSize: "11px", color: "#64748b" }}>({calcMin(stats.pulseVals)}–{calcMax(stats.pulseVals)})</span>
                            </td>
                          </>
                        ) : (
                          <td colSpan={3} style={{ padding: "10px 12px", textAlign: "center", fontStyle: "italic", color: "#94a3b8", fontSize: "12px" }}>
                            No readings
                          </td>
                        )
                      )}
                      <td style={{ padding: "10px 12px", minWidth: "140px" }}>
                        <div style={{ display: "flex", height: "8px", borderRadius: "4px", overflow: "hidden", background: "#f1f5f9" }}>
                          {totalSeg > 0 ? (
                            <>
                              <div style={{ width: `${(stats.above / totalSeg) * 100}%`, background: "#dc2626" }} />
                              <div style={{ width: `${(stats.within / totalSeg) * 100}%`, background: "#16a34a" }} />
                              <div style={{ width: `${(stats.below / totalSeg) * 100}%`, background: "#ea580c" }} />
                            </>
                          ) : (
                            <div style={{ width: "100%", background: "#f1f5f9" }} />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px", padding: "14px 16px", borderTop: "1px solid #f1f5f9", background: "#fafafa" }}>
              <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "4px" }}>ABOVE TARGET</div>
                {summaryPeriodData.kind === "glucose" ? (
                  <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Glucose: {formatPercentCount(summaryPeriodData.totals.above, summaryPeriodData.totals.total)}</div>
                ) : summaryPeriodData.kind === "weight" ? (
                  <>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Weight: {formatPercentCount(summaryPeriodData.totals.aboveWeight, summaryPeriodData.totals.totalWeight)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Body Fat: {formatPercentCount(summaryPeriodData.totals.aboveFat, summaryPeriodData.totals.totalFat)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>BMI: {formatPercentCount(summaryPeriodData.totals.aboveBmi, summaryPeriodData.totals.totalBmi)}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Systolic: {formatPercentCount(summaryPeriodData.totals.above.sys, summaryPeriodData.totals.sysTotal)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Diastolic: {formatPercentCount(summaryPeriodData.totals.above.dia, summaryPeriodData.totals.diaTotal)}</div>
                  </>
                )}
              </div>
              <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "4px" }}>WITHIN TARGET</div>
                {summaryPeriodData.kind === "glucose" ? (
                  <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Glucose: {formatPercentCount(summaryPeriodData.totals.within, summaryPeriodData.totals.total)}</div>
                ) : summaryPeriodData.kind === "weight" ? (
                  <>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Weight: {formatPercentCount(summaryPeriodData.totals.withinWeight, summaryPeriodData.totals.totalWeight)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Body Fat: {formatPercentCount(summaryPeriodData.totals.withinFat, summaryPeriodData.totals.totalFat)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>BMI: {formatPercentCount(summaryPeriodData.totals.withinBmi, summaryPeriodData.totals.totalBmi)}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Systolic: {formatPercentCount(summaryPeriodData.totals.within.sys, summaryPeriodData.totals.sysTotal)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Diastolic: {formatPercentCount(summaryPeriodData.totals.within.dia, summaryPeriodData.totals.diaTotal)}</div>
                  </>
                )}
              </div>
              <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#ea580c", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "4px" }}>BELOW TARGET</div>
                {summaryPeriodData.kind === "glucose" ? (
                  <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Glucose: {formatPercentCount(summaryPeriodData.totals.below, summaryPeriodData.totals.total)}</div>
                ) : summaryPeriodData.kind === "weight" ? (
                  <>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Weight: {formatPercentCount(summaryPeriodData.totals.belowWeight, summaryPeriodData.totals.totalWeight)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Body Fat: {formatPercentCount(summaryPeriodData.totals.belowFat, summaryPeriodData.totals.totalFat)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>BMI: {formatPercentCount(summaryPeriodData.totals.belowBmi, summaryPeriodData.totals.totalBmi)}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Systolic: {formatPercentCount(summaryPeriodData.totals.below.sys, summaryPeriodData.totals.sysTotal)}</div>
                    <div style={{ fontSize: "12px", color: "#334155", lineHeight: 1.5 }}>Diastolic: {formatPercentCount(summaryPeriodData.totals.below.dia, summaryPeriodData.totals.diaTotal)}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

          {!isWeight ? (
            <div className="pdx-panel">
              <div className="pdx-panel-head">
                <div className="pdx-panel-title">
                  Mean chart — {isGlucose ? "Blood glucose" : "Blood pressure"}
                </div>
              </div>
              {isGlucose ? (
                <MeanChartBG practiceId={practiceId} patientId={patientId} measurements={measurements} scheduleTargets={scheduleTargets} embedded={true} />
              ) : (
                <MeanChart practiceId={practiceId} patientId={patientId} measurements={measurements} scheduleTargets={scheduleTargets} embedded={true} />
              )}
            </div>
          ) : null}
            </div>
          )}
        </div>
      )}

      </div> {/* end rpm-overview-print-area */}

      {/* ADD VITAL DATA MODALS BASED ON ACTIVE VITAL TAB */}
      {showAddModal && isSelectedVitalAddable && isGlucose && (
        <AddBGForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          initialScheduleTargets={scheduleTargets}
          onSuccess={() => {
            setShowAddModal(false);
            fetchMeasurements();
            if (typeof onRefresh === 'function') onRefresh();
          }}
        />
      )}

      {showAddModal && isSelectedVitalAddable && isWeight && (
        <AddWForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          initialScheduleTargets={scheduleTargets}
          onSuccess={() => {
            setShowAddModal(false);
            fetchMeasurements();
            if (typeof onRefresh === 'function') onRefresh();
          }}
        />
      )}

      {showAddModal && isSelectedVitalAddable && !isGlucose && !isWeight && (
        <AddBPForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          initialScheduleTargets={scheduleTargets}
          modalClassName="bp-add-data-modal"
          onSuccess={() => {
            setShowAddModal(false);
            fetchMeasurements();
            if (typeof onRefresh === 'function') onRefresh();
          }}
        />
      )}

      {/* EMAIL MODAL */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultTo=""
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={handleSendEmail}
      />
    </div>
  );
};

export default PatientRpmOverview;
